import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Hls from 'hls.js';

import {
    MediaPlaybackManager,
    usePlaybackStore,
    AudioNodePool,
    TranslatedTimelineSegments,
} from '../videoTimeBox';

import { useSubtitleRenderer } from '../videoTextList';
import type { SubtitleOverlayPlayerRef } from '../../subtitleOverlayPlayer/SubtitleOverlayPlayer';

import { processVideoUrl, processTransAudioUrl } from '../../../utils/videoUrl';
import { TranslatedTrackBufferPlayer } from '../../../utils/translatedTrackBuffer';
import { createVideoFramePulse } from '../../../utils/videoFramePulse';
import { computeAudioSeekOffset, computeSubtitleMatchTime, createTranslatedAudioTiming } from '../../../utils/translatedAudioTiming';
import { parseTimeToSeconds, resolveSubtitleTimeWindow } from '../../../utils/time.utils';
import { audioManager } from '../../../utils/audioManager';
import { VIDEO_CDN_PREFIX } from '../../../config/video';
import { getTaskStatusApi } from '../../../service/task';

// Global audio instance tracker
let GLOBAL_PLAYING_AUDIO_ID: string | null = null;

const END_EPSILON = 0.1;

/** 编辑页左侧/时间轴使用的 HLS：优先合成流，无则与右侧一致回退到纯净流 */
function resolveEditPageStreamHlsUrl(data: Task.GetTaskStatusResult | null | undefined): string | null {
    if (!data) return null;
    const composite = data.video_hls_url ?? data.videoHlsUrl ?? null;
    const pure = data.pure_video_hls_url ?? data.pureVideoHlsUrl ?? null;
    const c = typeof composite === 'string' && composite.trim() ? composite : null;
    const p = typeof pure === 'string' && pure.trim() ? pure : null;
    return c ?? p ?? null;
}

type Subtitle = Task.SubtitlesLegacy;

export interface VideoShowControllerParams {
    videoData: Task.GetTaskStatusResult | null;
    setVideoData: React.Dispatch<React.SetStateAction<Task.GetTaskStatusResult | null>>;
    setVideoDataSafe: (data: Task.GetTaskStatusResult | null) => void;
    id: string | undefined;
    isFromPure: boolean;
    showError: (msg: string) => void;
    showSuccess: (msg: string) => void;
    showWarning: (msg: string) => void;
    // From timebox
    timelineEditorRef: React.MutableRefObject<any>;
    timelineScrubbingRef: React.MutableRefObject<boolean>;
    translatedTrackSegmentsRef: React.MutableRefObject<any[]>;
    translatedTrackBuildSignature: string;
    setPlaybackUiTime: (t: number) => void;
    currentTimeRef: React.MutableRefObject<number>;
    playheadTotalTimeRef: React.MutableRefObject<number>;
    // From textList
    targetText: any[];
    sourcesText: any[];
    highlightedSubtitleId: number | null;
    updateTargetSubtitleHighlight: (time: number) => void;
    setHighlightedSubtitleId: (id: number | null) => void;
    // From timebox
    processedSubtitlesRef: React.MutableRefObject<Array<{ subtitle: Task.SubtitlesLegacy; startTime: number; endTime: number; id: number }>>;
    scrollToHighlightedSubtitle: (id: number) => void;
    volumeMap: Map<number, number>;
    pendingTimelineUpdates: Map<number, { audio_speed: number; post_start_time: string; post_end_time: string }>;
}

export const useVideoShowController = ({
    videoData,
    setVideoData,
    setVideoDataSafe,
    id,
    isFromPure,
    showError,
    showSuccess,
    showWarning,
    timelineEditorRef,
    timelineScrubbingRef,
    translatedTrackSegmentsRef,
    translatedTrackBuildSignature,
    setPlaybackUiTime,
    currentTimeRef,
    playheadTotalTimeRef,
    targetText,
    sourcesText,
    highlightedSubtitleId,
    updateTargetSubtitleHighlight,
    setHighlightedSubtitleId,
    processedSubtitlesRef,
    scrollToHighlightedSubtitle,
    volumeMap,
    pendingTimelineUpdates,
}: VideoShowControllerParams) => {
    const handleSwitchTrack = useCallback((trackType: 'translated' | 'original') => {
        if (timelineEditorRef.current) {
            const previousTrack = timelineEditorRef.current.activeTrack;
            timelineEditorRef.current.setActiveTrack(trackType);
            
            // 切换轨道时，停止/开始相应的音频
            if (previousTrack === 'translated' && trackType === 'original') {
                // 从"翻译后"切换到"原视频"：先完全停止翻译音频，再开始原人声音频
                translatedTrackPlayerRef.current?.pause();
                stopTransAudio();
                if (transAudioRef.current) {
                    transAudioRef.current.pause();
                    transAudioRef.current.currentTime = 0;
                    setCurrentTransAudio(null);
                }
                // 清除当前播放的字幕ID
                currentPlayingSubtitleIdRef.current = null;
                
                // 如果正在播放，初始化并播放原人声音频
                // 注意：isPlaying 不在依赖中，使用 sourceVideoRef.current 判断播放状态
                // 如果是点击台词导致的跳转，不自动播放
                const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                if (isCurrentlyPlaying && !isSubtitleClickSeekRef.current && videoData?.vocals_hls_url) {
                    playAudioTracks(); // 重新初始化音频轨道
                }
            } else if (previousTrack === 'original' && trackType === 'translated') {
                // 从"原视频"切换到"翻译后"：先完全停止原人声音频，再开始翻译音频
                // 关键补充：原视频轨道可能正在播放“当句原始台词音频”（originAudioRef），也必须停止
                stopOriginAudio();
                stopCurrentPlayingInstance();
                if (vocalsAudioRef.current) {
                    vocalsAudioRef.current.pause();
                    vocalsAudioRef.current.currentTime = 0;
                }
                if (vocalsHlsRef.current) {
                    vocalsHlsRef.current.destroy();
                    vocalsHlsRef.current = null;
                }
                // 如果是点击台词导致的跳转，不自动播放翻译音频
                const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                if (isCurrentlyPlaying && !isSubtitleClickSeekRef.current && subtitlePlayEndTimeRef.current === null) {
                    const player = translatedTrackPlayerRef.current;
                    if (player) {
                        void player.play(sourceVideoRef.current?.currentTime || 0).catch(error => {
                            console.error('切换轨道后恢复连续翻译音轨失败:', error);
                        });
                    }
                }
            }
        }
    }, [videoData]);

    // 播放头移动的回调函数（优化版本）
    // 用于处理时间轴空白处点击：播放状态下跳转并播放，暂停状态下跳转并静默
    const handlePlayheadMove = useCallback((time: number) => {
        // 【核心修复】：如果是点击台词导致的跳转，不自动播放（完全静默）
        if (isSubtitleClickSeekRef.current) {
            // 只执行跳转，不播放
            if (sourceVideoRef.current) {
                sourceVideoRef.current.currentTime = time;
                setPlaybackUiTime(time);
            }
            if (subtitleOverlayPlayerRef.current) {
                playbackManagerRef.current?.notifyRightSeekStarted();
                subtitleOverlayPlayerRef.current.seekToTime(time);
            }
            // 同步音频进度（但不播放）
            if (backgroundAudioRef.current && backgroundAudioRef.current.src) {
                backgroundAudioRef.current.currentTime = time;
            }
            if (vocalsAudioRef.current && vocalsAudioRef.current.src) {
                vocalsAudioRef.current.currentTime = time;
            }
            seekTranslatedTrackBuffer(time, false);
            if (timelineEditorRef.current) {
                const te = timelineEditorRef.current;
                te.currentTime = time;
                if (te.playheadAnimationId) {
                    cancelAnimationFrame(te.playheadAnimationId);
                    te.playheadAnimationId = undefined;
                }
                te.updatePlayheadPosition({ syncVideo: false, scrollBehavior: "auto" });
            }
            return; // 直接返回，不执行后续播放逻辑
        }

        // 同步左侧视频播放进度
        if (sourceVideoRef.current) {
            sourceVideoRef.current.currentTime = time;
            setPlaybackUiTime(time);
        }
        
        // 同步右侧视频播放进度
        if (subtitleOverlayPlayerRef.current) {
            playbackManagerRef.current?.notifyRightSeekStarted();
            subtitleOverlayPlayerRef.current.seekToTime(time);
        }
        
        // 同步背景音播放进度到黑色刻度线位置（无论是否在播放都要同步）
        if (backgroundAudioRef.current && backgroundAudioRef.current.src) {
            backgroundAudioRef.current.currentTime = time;
            // 时间轴同步：背景音已同步到黑色刻度线位置
        }
        
        // 同步原人声音频播放进度到黑色刻度线位置（如果选中"原视频"轨道）
        const currentActiveTrack = timelineEditorRef.current?.activeTrack || 'translated';
        if (currentActiveTrack === 'original' && vocalsAudioRef.current && vocalsAudioRef.current.src) {
            vocalsAudioRef.current.currentTime = time;
            // 时间轴同步：原人声音频已同步到黑色刻度线位置
        }
        
        // 【核心修复】：根据播放状态决定是否播放音频
        // 播放状态下：跳转并继续播放
        // 暂停状态下：只跳转，不播放（静默）
        // 使用 sourceVideoRef 判断播放状态，避免依赖项问题
        const currentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
        if (currentlyPlaying) {
            // 播放状态下，确保视频继续播放
            if (sourceVideoRef.current && sourceVideoRef.current.paused) {
                sourceVideoRef.current.play().catch(error => {
                    console.error('恢复视频播放失败:', error);
                });
            }
            if (subtitleOverlayPlayerRef.current) {
                try { subtitleOverlayPlayerRef.current.play(); } catch {}
            }
            if (backgroundAudioRef.current && backgroundAudioRef.current.src && backgroundAudioRef.current.paused) {
                backgroundAudioRef.current.play().catch(error => {
                    console.error('恢复背景音播放失败:', error);
                });
            }
            if (currentActiveTrack === 'original' && vocalsAudioRef.current && vocalsAudioRef.current.src && vocalsAudioRef.current.paused) {
                vocalsAudioRef.current.play().catch(error => {
                    console.error('恢复人声音频播放失败:', error);
                });
            }
            if (currentActiveTrack === 'translated') {
                seekTranslatedTrackBuffer(time, true);
            }
        } else {
            // 暂停状态下，设置拦截标记，确保不自动播放
            isSubtitleClickSeekRef.current = true;
            // 确保视频保持暂停
            if (sourceVideoRef.current && !sourceVideoRef.current.paused) {
                sourceVideoRef.current.pause();
            }
            if (subtitleOverlayPlayerRef.current) {
                try { subtitleOverlayPlayerRef.current.pause(); } catch {}
            }
            // 停止所有音频
            stopTransAudio();
            stopOriginAudio();
            stopCurrentPlayingInstance();
            if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
                backgroundAudioRef.current.pause();
            }
            if (vocalsAudioRef.current && !vocalsAudioRef.current.paused) {
                vocalsAudioRef.current.pause();
            }
            seekTranslatedTrackBuffer(time, false);
            // 短暂延迟后清除标记，允许后续的正常操作
            setTimeout(() => {
                isSubtitleClickSeekRef.current = false;
            }, 500);
        }
        
        // 更新进度条显示（缓存DOM元素）
        if (duration > 0) {
            const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
            if (progressBar) {
                const progressPercent = (time / duration) * 100;
                progressBar.style.setProperty('--progress', `${progressPercent}%`);
            }
        }

        // 时间轴播放头与主视频时间对齐（拖轴松手后内部 currentTime 已与 time 一致，写回可消除与 store 的偏差）
        if (timelineEditorRef.current) {
            const te = timelineEditorRef.current;
            te.currentTime = time;
            if (te.playheadAnimationId) {
                cancelAnimationFrame(te.playheadAnimationId);
                te.playheadAnimationId = undefined;
            }
            te.updatePlayheadPosition({ syncVideo: false, scrollBehavior: "auto" });
        }

        // 高亮对应的目标台词
        updateTargetSubtitleHighlight(time);
    }, []);

    const [isLoadingVideo, setIsLoadingVideo] = useState(true);

    const [showBackgroundVolumePopup, setShowBackgroundVolumePopup] = useState(false);
    const [backgroundVolumePopupPosition, setBackgroundVolumePopupPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const [backgroundVolume, setBackgroundVolume] = useState<number>(1);
    const backgroundVolumeRef = useRef(backgroundVolume);
    useEffect(() => {
        backgroundVolumeRef.current = backgroundVolume;
    }, [backgroundVolume]);

    const [isPreloaded, setIsPreloaded] = useState(false);
    const [leftVideoReady, setLeftVideoReady] = useState(false);
    const [rightVideoReady, setRightVideoReady] = useState(false);
    
    // 背景音控制状态
    const [backgroundAudioEnabled, setBackgroundAudioEnabled] = useState(true);
    

    const [isPlaying, setIsPlaying] = useState(false);
    const isPlayingRef = useRef(false);
    const playbackManagerRef = useRef<MediaPlaybackManager | null>(null);
    const [duration, setDuration] = useState(0);

    /** 底部时间、进度条与时间轴播放头与指定时刻对齐（不 seek 视频，避免与 handlePlayheadMove 重复） */
    const syncPlaybackChromeToTime = useCallback(
        (time: number) => {
            setPlaybackUiTime(time);
            const v = sourceVideoRef.current;
            const dur = (v && Number.isFinite(v.duration) && v.duration > 0 ? v.duration : duration) || 0;
            if (dur > 0) {
                const progressBar = document.querySelector(".progress-bar") as HTMLInputElement;
                if (progressBar) {
                    progressBar.style.setProperty(
                        "--progress",
                        `${Math.min(100, Math.max(0, (time / dur) * 100))}%`
                    );
                }
            }
            const te = timelineEditorRef.current;
            if (te) {
                te.currentTime = time;
                if (te.playheadAnimationId) {
                    cancelAnimationFrame(te.playheadAnimationId);
                    te.playheadAnimationId = undefined;
                }
                te.updatePlayheadPosition({ syncVideo: false, scrollBehavior: "auto" });
            }
        },
        [duration, setPlaybackUiTime]
    );

    /** 片尾已执行过强制收尾，避免 ended/timeupdate 重复触发 */
    const playbackFinishedRef = useRef(false);
    const lastObservedVideoTimeRef = useRef(0);
    const handleVideoEndedRef = useRef<() => void>(() => {});

    const resetPlaybackFinished = useCallback(() => {
        playbackFinishedRef.current = false;
        lastObservedVideoTimeRef.current = 0;
    }, []);

    // 让右侧字幕播放组件订阅到最新播放状态
    useEffect(() => {
        usePlaybackStore.getState().setIsPlaying(isPlaying);
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    const [rightVideoIsPlaying, setRightVideoIsPlaying] = useState(false);
    const [rightVideoCurrentTime, setRightVideoCurrentTime] = useState(0);
    const [rightVideoDuration, setRightVideoDuration] = useState(0);
    // 标记是否为用户手动操作
    const userInteractionRef = useRef(false);
    // 台词播放状态
    const [subtitlePlayEndTime, setSubtitlePlayEndTime] = useState<number | null>(null);
    const subtitlePlayEndTimeRef = useRef<number | null>(null);
    const [justFinishedSubtitlePlay, setJustFinishedSubtitlePlay] = useState(false);
    const subtitlePlayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // 音频就绪状态跟踪
    const [audioReadyState, setAudioReadyState] = useState<{
        backgroundReady: boolean;
        vocalsReady: boolean;
    }>({ backgroundReady: false, vocalsReady: false });
    const audioReadyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // highlightedSubtitleId and setHighlightedSubtitleId come from textList via params
    // 左/右视频原始分辨率与宽高比
    const [leftVideoAspect, setLeftVideoAspect] = useState<{ width: number; height: number; ratio: number; simplified: string } | null>(null);
    const [rightVideoAspect, setRightVideoAspect] = useState<{ width: number; height: number; ratio: number; simplified: string } | null>(null);
    const [volume, setVolume] = useState(1);

    const sourceVideoRef = useRef<HTMLVideoElement>(null);
    /** 时间轴拖拽/scrub 中：暂停 PlayheadController 与视频时间的同步，避免抖动 */

    const targetVideoRef = useRef<HTMLVideoElement>(null);
    const subtitleOverlayPlayerRef = useRef<SubtitleOverlayPlayerRef>(null);

    const subtitleCanvasRef = useRef<HTMLCanvasElement>(null);
    const hlsRef = useRef<any>(null);
    const currentVideoUrlRef = useRef<string>('');

    /** 切换任务或直达 URL 进入时重置双路就绪与左侧 URL 缓存，避免沿用旧任务或 initLeftVideo 误判「URL 未变」而跳过 */
    useEffect(() => {
        setLeftVideoReady(false);
        setRightVideoReady(false);
        currentVideoUrlRef.current = '';
    }, [id]);
    
    // 视频同步相关
    const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastSyncTimeRef = useRef<number>(0);
    const syncThreshold = 0.05; // 50毫秒的同步阈值
    const lastAudioCheckTimeRef = useRef<number>(0); // 上次检查音频播放的时间
    const lastAudioCheckVideoTimeRef = useRef<number>(0); // 记录视频时间，用于检测 Seek 跳变
    const isSubtitleClickSeekRef = useRef<boolean>(false); // 标记是否是点击台词导致的跳转（不自动播放）
    // 上下键防抖：避免连发/手抖导致连跳多句
    const lastVideoEditKeyTimeRef = useRef<number>(0);
    // 当前播放的字幕ID（状态控制，避免重复播放）
    const currentPlayingSubtitleIdRef = useRef<number | null>(null);
    
    // 全局唯一播放实例管理
    type PlayingInstanceType = 'origin' | 'target' | null;
    interface PlayingInstance {
        type: PlayingInstanceType;
        audioId?: string; // 翻译后台词的 audioId
        subtitleId?: number; // 字幕ID
        timeRange?: string; // 时间范围
    }
    const currentPlayingInstanceRef = useRef<PlayingInstance | null>(null);

    const stopCurrentPlayingInstance = useCallback(() => {
        const instance = currentPlayingInstanceRef.current;
        if (!instance) {
            return;
        }
        
        // 全局播放管理：停止当前播放实例
        
        if (instance.type === 'origin') {
            // 停止原台词播放
            if (originAudioRef.current) {
                originAudioRef.current.pause();
                originAudioRef.current.currentTime = 0;
                originAudioRef.current.src = '';
            }
            // 停止视频播放
            if (sourceVideoRef.current) {
                sourceVideoRef.current.pause();
            }
            if (subtitleOverlayPlayerRef.current) {
                try { subtitleOverlayPlayerRef.current.pause(); } catch {}
            }
            // 清除播放状态
            setSubtitlePlayEndTime(null);
            setIsPlaying(false);
            // 清除定时器
            if (subtitlePlayTimeoutRef.current) {
                clearTimeout(subtitlePlayTimeoutRef.current);
                subtitlePlayTimeoutRef.current = null;
            }
        } else if (instance.type === 'target') {
            // 停止翻译后台词播放
            if (instance.audioId) {
                // 使用 AudioManager 播放的情况
                try {
                    audioManager.stopSound(instance.audioId);
                    audioManager.destroySound(instance.audioId);
                } catch (error) {
                    console.error('停止 AudioManager 音频时出错:', error);
                }
            } else {
                // 回退模式：使用 transAudioRef 播放的情况
                if (transAudioRef.current) {
                    transAudioRef.current.pause();
                    transAudioRef.current.currentTime = 0;
                    transAudioRef.current.src = '';
                }
            }
            // 停止视频播放
            if (sourceVideoRef.current) {
                sourceVideoRef.current.pause();
            }
            if (subtitleOverlayPlayerRef.current) {
                try { subtitleOverlayPlayerRef.current.pause(); } catch {}
            }
            // 清除播放状态
            setSubtitlePlayEndTime(null);
            setIsPlaying(false);
            setCurrentTransAudio(null);
            if (instance.subtitleId !== undefined) {
                currentPlayingSubtitleIdRef.current = null;
            }
            // 清除定时器
            if (subtitlePlayTimeoutRef.current) {
                clearTimeout(subtitlePlayTimeoutRef.current);
                subtitlePlayTimeoutRef.current = null;
            }
        }
        
        // 停止背景音
        if (backgroundAudioRef.current) {
            backgroundAudioRef.current.pause();
        }
        
        // 清除当前播放实例记录
        currentPlayingInstanceRef.current = null;
    }, []);

    // 确保背景音的 Web Audio 增益节点已初始化（resume 请在用户手势路径单独调用）
    const ensureBackgroundGainNode = useCallback(() => {
        if (!backgroundAudioRef.current) {
            return;
        }

        try {
            if (!audioNodePoolRef.current) {
                audioNodePoolRef.current = new AudioNodePool();
            }
            const vol = Number.isFinite(backgroundVolumeRef.current) ? backgroundVolumeRef.current : 1.0;
            const { context, gain, source } = audioNodePoolRef.current.ensureMediaGainNode(
                'background',
                backgroundAudioRef.current,
                vol
            );
            backgroundAudioContextRef.current = context;
            backgroundGainNodeRef.current = gain;
            backgroundSourceNodeRef.current = source;
        } catch (error) {
            console.error('初始化背景音 Web Audio 放大器失败:', error);
        }
    }, []);
    

    const backgroundAudioRef = useRef<HTMLAudioElement>(null);
    const originAudioRef = useRef<HTMLAudioElement>(null); // 原始台词音频
    const transAudioRef = useRef<HTMLAudioElement>(null); // 翻译音频
    const vocalsAudioRef = useRef<HTMLAudioElement>(null); // 原人声音频（vocals_hls_url）
    const backgroundHlsRef = useRef<any>(null);
    const vocalsHlsRef = useRef<any>(null); // 原人声音频的HLS实例
    // 背景音 Web Audio 放大器（GainNode）相关引用
    const backgroundAudioContextRef = useRef<AudioContext | null>(null);
    const backgroundGainNodeRef = useRef<GainNode | null>(null);
    const backgroundSourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
    const audioNodePoolRef = useRef<AudioNodePool | null>(null);
    const audioPlayRequestedRef = useRef<boolean>(false);

    const resumeBackgroundAudioContextIfSuspended = useCallback(() => {
        const ctx = backgroundAudioContextRef.current;
        if (!ctx || ctx.state !== 'suspended') {
            return;
        }
        void ctx.resume().catch(() => {
            // 仍无手势时忽略
        });
    }, []);

    useEffect(() => {
        return () => {
            // 清理 60fps 毫秒级同步定时器，避免组件卸载后 setInterval 继续运行导致内存泄漏
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
            if (!audioNodePoolRef.current) {
                return;
            }
            void audioNodePoolRef.current.disposeAll(true);
            audioNodePoolRef.current = null;
            backgroundAudioContextRef.current = null;
            backgroundGainNodeRef.current = null;
            backgroundSourceNodeRef.current = null;
        };
    }, []);
    
    // 当前播放的音频状态
    const [currentSubtitleAudio, setCurrentSubtitleAudio] = useState<string | null>(null);
    const [currentTransAudio, setCurrentTransAudio] = useState<string | null>(null);
    const [translatedTrackBuildState, setTranslatedTrackBuildState] = useState<'idle' | 'building' | 'ready' | 'error'>('idle');
    const [translatedTrackBuildError, setTranslatedTrackBuildError] = useState<string | null>(null);
    const translatedTrackPlayerRef = useRef<TranslatedTrackBufferPlayer | null>(null);

    const translatedTrackBuildTokenRef = useRef(0);

    useEffect(() => {
        subtitlePlayEndTimeRef.current = subtitlePlayEndTime;
    }, [subtitlePlayEndTime]);

    // 等待左侧视频 seek 到指定时间
    const waitForLeftSeek = (targetTime: number, epsilon: number = 0.02, timeoutMs: number = 1500) => {
        return new Promise<void>((resolve) => {
            const video = sourceVideoRef.current;
            if (!video) { resolve(); return; }

            const done = () => resolve();
            const start = performance.now();

            const check = () => {
                if (!sourceVideoRef.current) { resolve(); return; }
                const ct = sourceVideoRef.current.currentTime || 0;
                if (Math.abs(ct - targetTime) <= epsilon) { done(); return; }
                if (performance.now() - start > timeoutMs) { done(); return; }
                requestAnimationFrame(check);
            };

            const onSeeked = () => {
                try { video.removeEventListener('seeked', onSeeked); } catch {}
                done();
            };
            try { video.addEventListener('seeked', onSeeked, { once: true } as any); } catch { /* ignore */ }
            check();
        });
    };

    // 等待右侧视频 seek 到指定时间
    const waitForRightSeek = (targetTime: number, epsilon: number = 0.02, timeoutMs: number = 1500) => {
        return new Promise<void>((resolve) => {
            const right = subtitleOverlayPlayerRef.current;
            if (!right) { resolve(); return; }
            const start = performance.now();
            const check = () => {
                const ct = (right.getCurrentTime && right.getCurrentTime()) || 0;
                if (Math.abs(ct - targetTime) <= epsilon) { resolve(); return; }
                if (performance.now() - start > timeoutMs) { resolve(); return; }
                requestAnimationFrame(check);
            };
            check();
        });
    };

    const waitBothSeeked = async (targetTime: number) => {
        await Promise.all([waitForLeftSeek(targetTime), waitForRightSeek(targetTime)]);
    };

    // 毫秒级视频同步函数
    const startMillisecondSync = () => {
        if (syncIntervalRef.current) {
            clearInterval(syncIntervalRef.current);
        }
        
        syncIntervalRef.current = setInterval(() => {
            if (!sourceVideoRef.current || !subtitleOverlayPlayerRef.current || !isPlaying) {
                return;
            }
            
            const leftTime = sourceVideoRef.current.currentTime;
            const rightTime = subtitleOverlayPlayerRef.current.getCurrentTime();
            const timeDiff = Math.abs(leftTime - rightTime);
            
            // 如果时间差超过阈值，进行同步
            if (timeDiff > syncThreshold) {
                // 毫秒级同步
                
                // 以左侧视频为基准，同步右侧视频
                if (subtitleOverlayPlayerRef.current.seekToTime) {
                    playbackManagerRef.current?.notifyRightSeekStarted();
                    subtitleOverlayPlayerRef.current.seekToTime(leftTime);
                }
                
                lastSyncTimeRef.current = Date.now();
            }
        }, 16); // 约60fps的同步频率
    };
    
    const stopMillisecondSync = () => {
        if (syncIntervalRef.current) {
            clearInterval(syncIntervalRef.current);
            syncIntervalRef.current = null;
        }
    };
    
    // 精确同步启动函数
    const startPreciseSync = async (startTime: number) => {
        // 先停止之前的同步
        stopMillisecondSync();
        
        // 同时跳转到精确时间点，但不开始播放
        if (sourceVideoRef.current) {
            sourceVideoRef.current.currentTime = startTime;
            sourceVideoRef.current.pause(); // 确保左侧视频暂停
        }
        
        if (subtitleOverlayPlayerRef.current) {
            playbackManagerRef.current?.notifyRightSeekStarted();
            subtitleOverlayPlayerRef.current.seekToTime(startTime);
            subtitleOverlayPlayerRef.current.pause(); // 确保右侧视频暂停
        }
        
        // 等待两个视频都到达目标时间点
        await new Promise<void>((resolve) => {
            const checkSync = () => {
                const leftTime = sourceVideoRef.current?.currentTime || 0;
                const rightTime = subtitleOverlayPlayerRef.current?.getCurrentTime() || 0;
                const leftDiff = Math.abs(leftTime - startTime);
                const rightDiff = Math.abs(rightTime - startTime);
                
                if (leftDiff < 0.01 && rightDiff < 0.01) {
                    // 精确同步完成
                    resolve();
                } else {
                    setTimeout(checkSync, 5); // 5ms检查一次
                }
            };
            checkSync();
        });
        
        // 启动毫秒级同步
        startMillisecondSync();
    };

    // 这些函数现在由每行的 RowItem 组件独立处理

    // 播放原始台词音频
    const playOriginAudio = (timeRange: string) => {
        if (!videoData || !videoData.subtitles) {
            // 没有字幕数据，无法播放原始音频
            return;
        }

        // 查找对应的字幕数据
        const matchingSubtitle = videoData.subtitles.find(subtitle => {
            const subtitleTimeRange = `${subtitle.origin_start_time} → ${subtitle.origin_end_time}`;
            return subtitleTimeRange === timeRange;
        });

        if (!matchingSubtitle || !matchingSubtitle.origin_audio) {
            // 未找到对应的原始音频
            return;
        }

        // 构建原始音频URL（支持绝对路径和相对路径）
        const originAudioUrl = processVideoUrl(matchingSubtitle.origin_audio);
        // 播放原始台词音频

        // 创建或更新音频元素
        if (!originAudioRef.current) {
            originAudioRef.current = new Audio();
        }

        const originAudio = originAudioRef.current;
        
        // 设置音频源
        if (originAudio.src !== originAudioUrl) {
            originAudio.src = originAudioUrl;
        }

        // 获取语速设置
        // 如果是原始音频origin_audio，不应用倍速
        const audioSpeed = 1.0; // 原始音频不应用倍速

        // 设置音频属性
        originAudio.volume = 1.0;
        originAudio.currentTime = 0;
        // 原始音频不应用倍速
        originAudio.playbackRate = audioSpeed;

        // 等待音频加载完成后再播放
        const playAudioWhenReady = () => {
            let hasPlayed = false; // 防止重复播放的标志
            
            const playAudio = () => {
                if (hasPlayed) return; // 如果已经播放过，直接返回
                hasPlayed = true;
                
                // 确保播放速率为台词数据中的audio_speed值
                originAudio.playbackRate = audioSpeed;
                
                originAudio.play().then(() => {
                    // 原始台词音频播放成功
                }).catch(error => {
                    console.error('原始台词音频播放失败:', error);
                });
            };
            
            if (originAudio.readyState >= 2) { // HAVE_CURRENT_DATA
                playAudio();
            } else {
                // 等待音频加载
                const handleCanPlay = () => {
                    originAudio.removeEventListener('canplay', handleCanPlay);
                    playAudio();
                };
                originAudio.addEventListener('canplay', handleCanPlay);
                
                // 超时保护，如果5秒内没有加载完成，强制播放
                setTimeout(() => {
                    originAudio.removeEventListener('canplay', handleCanPlay);
                    playAudio();
                }, 5000);
            }
        };

        playAudioWhenReady();
    };

    // 停止原始台词音频
    const stopOriginAudio = () => {
        if (originAudioRef.current) {
            originAudioRef.current.pause();
            originAudioRef.current.currentTime = 0;
            // 原始台词音频已停止
        }
    };

    // 切换背景音控制

    const toggleBackgroundAudio = useCallback(() => {
        // 点击瞬间先接好节点再 resume（手势栈内）
        ensureBackgroundGainNode();
        const ctx = backgroundAudioContextRef.current;
        if (ctx?.state === 'suspended') {
            void ctx.resume().then(() => {
                console.log('AudioContext 已手动唤醒');
            });
        }

        setBackgroundAudioEnabled(prev => !prev);
    }, [ensureBackgroundGainNode]);

    // ✅ 监听背景音开关状态，处理播放/暂停
    useEffect(() => {
        const bgAudio = backgroundAudioRef.current;
        const mainVideo = sourceVideoRef.current;

        if (!bgAudio || !mainVideo) return;

        const handleAudioStateChange = async () => {
            if (backgroundAudioEnabled) {
                // 1. 打开时：确保 Web Audio 节点已连接（不在此处 resume，避免被动 effect 触发策略警告）
                ensureBackgroundGainNode();

                // 2. 同步时间（非常重要，否则音画不同步）
                bgAudio.currentTime = mainVideo.currentTime;

                // 3. 只有当主视频正在播放时，背景音才自动播放
                if (!mainVideo.paused) {
                    try {
                        await bgAudio.play();
                        console.log('背景音已恢复播放');
                    } catch (error) {
                        console.error('背景音恢复播放失败:', error);
                    }
                }
            } else {
                // 4. 关闭时：直接暂停
                bgAudio.pause();
            }
        };

        handleAudioStateChange();
    }, [backgroundAudioEnabled, ensureBackgroundGainNode]);

    // ✅ 补充：实时监听音量变化，确保 Web Audio 增益值正确
    useEffect(() => {
        if (backgroundGainNodeRef.current && backgroundAudioEnabled) {
            // 确保 backgroundVolume 是有效数值
            const volume = Number.isFinite(backgroundVolume) ? backgroundVolume : 1.0;
            // 立即应用音量
            backgroundGainNodeRef.current.gain.setValueAtTime(volume, backgroundAudioContextRef.current?.currentTime || 0);
        }
    }, [backgroundVolume, backgroundAudioEnabled]);

    const pauseTranslatedTrackBuffer = useCallback(() => {
        translatedTrackPlayerRef.current?.pause();
    }, []);

    const seekTranslatedTrackBuffer = useCallback((time: number, resume: boolean = false) => {
        const player = translatedTrackPlayerRef.current;
        if (!player) {
            return;
        }

        void player.seek(time, resume).catch(error => {
            console.error('同步连续翻译音轨失败:', error);
        });
    }, []);

    const playTranslatedTrackBuffer = useCallback((time: number) => {
        const player = translatedTrackPlayerRef.current;
        if (!player || !player.isReady()) {
            return;
        }

        void player.play(time).catch(error => {
            console.error('播放连续翻译音轨失败:', error);
        });
    }, []);

    const rebuildTranslatedTrackBuffer = useCallback(async () => {
        const player = translatedTrackPlayerRef.current;
        if (!player) {
            return;
        }

        const buildToken = ++translatedTrackBuildTokenRef.current;
        setTranslatedTrackBuildState('building');
        setTranslatedTrackBuildError(null);

        try {
            await player.rebuild(translatedTrackSegmentsRef.current, duration || 0);

            if (buildToken !== translatedTrackBuildTokenRef.current) {
                return;
            }

            setTranslatedTrackBuildState('ready');

            const video = sourceVideoRef.current;
            const activeTrack = timelineEditorRef.current?.activeTrack || 'translated';
            const shouldResume = !!video && !video.paused && activeTrack === 'translated' && subtitlePlayEndTimeRef.current === null;
            const currentVideoTime = video?.currentTime || 0;

            if (shouldResume) {
                playTranslatedTrackBuffer(currentVideoTime);
            } else {
                seekTranslatedTrackBuffer(currentVideoTime, false);
            }
        } catch (error: any) {
            if (buildToken !== translatedTrackBuildTokenRef.current) {
                return;
            }

            console.error('重建连续翻译音轨失败:', error);
            setTranslatedTrackBuildState('error');
            setTranslatedTrackBuildError(error?.message || '重建连续翻译音轨失败');
        }
    }, [duration, playTranslatedTrackBuffer, seekTranslatedTrackBuffer, translatedTrackBuildSignature]);

    useEffect(() => {
        translatedTrackPlayerRef.current = new TranslatedTrackBufferPlayer({
            edgeFadeSeconds: 0.008,
            fadeCurve: 'cosine',
        });

        return () => {
            translatedTrackPlayerRef.current?.destroy();
            translatedTrackPlayerRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!translatedTrackPlayerRef.current) {
            return;
        }

        void rebuildTranslatedTrackBuffer();
    }, [rebuildTranslatedTrackBuffer]);

    useEffect(() => {
        const video = sourceVideoRef.current;
        if (!video) {
            return;
        }

        const syncFromVideo = () => {
            const activeTrack = timelineEditorRef.current?.activeTrack || 'translated';
            const shouldResume = !video.paused && activeTrack === 'translated' && subtitlePlayEndTimeRef.current === null;
            seekTranslatedTrackBuffer(video.currentTime || 0, shouldResume);
        };

        const handlePlay = () => {
            if ((timelineEditorRef.current?.activeTrack || 'translated') === 'translated' && subtitlePlayEndTimeRef.current === null) {
                playTranslatedTrackBuffer(video.currentTime || 0);
            }
        };

        const handlePause = () => {
            pauseTranslatedTrackBuffer();
        };

        const handleEnded = () => {
            handleVideoEndedRef.current();
        };

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('seeked', syncFromVideo);
        video.addEventListener('ended', handleEnded);

        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('seeked', syncFromVideo);
            video.removeEventListener('ended', handleEnded);
        };
    }, [
        pauseTranslatedTrackBuffer,
        playTranslatedTrackBuffer,
        seekTranslatedTrackBuffer,
        videoData?.id,
    ]);

    // 片尾兜底：Chrome/Safari + Hls.js 可能不触发 ended 或 pause，用 timeupdate 检测回绕
    useEffect(() => {
        const video = sourceVideoRef.current;
        if (!video) return;

        const END_EPSILON = 0.08;

        const onTimeUpdate = () => {
            if (playbackFinishedRef.current || subtitlePlayEndTimeRef.current !== null) {
                return;
            }

            const dur = video.duration;
            const t = video.currentTime;
            const last = lastObservedVideoTimeRef.current;

            if (Number.isFinite(dur) && dur > 0) {
                if (last > dur * 0.85 && t < last - 0.25) {
                    handleVideoEndedRef.current();
                    return;
                }
                if (t >= dur - END_EPSILON && (video.ended || !video.paused)) {
                    handleVideoEndedRef.current();
                    return;
                }
            }

            lastObservedVideoTimeRef.current = t;
        };

        const onPlay = () => resetPlaybackFinished();

        video.addEventListener('timeupdate', onTimeUpdate);
        video.addEventListener('play', onPlay);

        return () => {
            video.removeEventListener('timeupdate', onTimeUpdate);
            video.removeEventListener('play', onPlay);
        };
    }, [resetPlaybackFinished, videoData?.id]);

    // ======================
    // 翻译音频预取窗口（Prefetch Window）
    // 目标：边播放边预加载，始终维持后续 5 条翻译音频就绪
    // ======================
    const PREFETCH_COUNT = 5;
    const PREFETCH_CACHE_LIMIT = 15; // 防止内存膨胀，保留最近的若干条（含窗口+一定冗余）
    const PREFETCH_MAX_INFLIGHT = 5; // 限制并发：提升到5以确保窗口内5条能尽快进入缓存
    const PREFETCH_THROTTLE_MS = 250; // 节流，避免过于频繁触发
    const translatedAudioTiming = useMemo(
        () =>
            createTranslatedAudioTiming({
                detectIntervalSec: 0.02,
                leadSec: 0.12,
                outputLatencySec: 0.12,
                backfillSec: 0.06,
            }),
        []
    );
    const VIDEO_SYNC_DRIFT_THRESHOLD_SEC = 0.25;
    const VIDEO_SYNC_UI_UPDATE_INTERVAL_MS = 120;

    const prefetchedTransAudiosRef = useRef<Map<string, HTMLAudioElement>>(new Map());
    const prefetchLoadingUrlsRef = useRef<Set<string>>(new Set());
    const prefetchInFlightRef = useRef<number>(0);
    const prefetchLastRunAtRef = useRef<number>(0);
    const currentTransAudioRef = useRef<string | null>(null);
    const lastFinishedTransSubtitleIdRef = useRef<number | null>(null);
    const currentPlayingSubtitleWindowRef = useRef<{
        id: number;
        startTime: number;
        endTime: number;
    } | null>(null);
    useEffect(() => {
        currentTransAudioRef.current = currentTransAudio;
    }, [currentTransAudio]);

    useEffect(() => {
        return () => {
            // 统一释放预取缓存，避免 Audio 对象和网络句柄堆积
            const map = prefetchedTransAudiosRef.current;
            for (const audio of map.values()) {
                try {
                    audio.pause();
                    audio.removeAttribute('src');
                    audio.load();
                } catch {
                    // ignore
                }
            }
            map.clear();
            prefetchLoadingUrlsRef.current.clear();
            prefetchInFlightRef.current = 0;
            if (window.prefetchedTransAudios === map) {
                window.prefetchedTransAudios = undefined;
            }
        };
    }, []);

    const getTranslatedSubtitleWindow = useCallback((subtitle: Task.SubtitlesLegacy | null | undefined) => {
        if (!subtitle) return null;

        const targetData = targetText.find(item => item.id === subtitle.id);
        return resolveSubtitleTimeWindow(subtitle, targetData);
    }, [targetText]);

    const maintainPrefetchWindow = useCallback((currentTimeSec: number) => {
        const now = Date.now();
        if (now - prefetchLastRunAtRef.current < PREFETCH_THROTTLE_MS) return;
        prefetchLastRunAtRef.current = now;

        const processed = processedSubtitlesRef.current || [];
        if (processed.length === 0) return;

        // 找到第一个 startTime >= currentTime 的下标（二分）
        let lo = 0;
        let hi = processed.length;
        while (lo < hi) {
            const mid = (lo + hi) >>> 1;
            if (processed[mid].startTime < currentTimeSec) {
                lo = mid + 1;
            } else {
                hi = mid;
            }
        }

        const windowItems = processed.slice(lo, lo + PREFETCH_COUNT);
        if (windowItems.length === 0) return;

        const requiredUrls: string[] = [];
        for (const item of windowItems) {
            const transAudio = item.subtitle?.trans_audio;
            if (!transAudio || !transAudio.trim()) continue;
            requiredUrls.push(processTransAudioUrl(transAudio));
        }

        const requiredSet = new Set<string>(requiredUrls);
        // 保护：不要把当前正在播放/当前 audio 元素 src 淘汰掉
        const playingUrl = currentTransAudioRef.current;
        if (playingUrl) requiredSet.add(playingUrl);
        const currentAudioSrc = transAudioRef.current?.src;
        if (currentAudioSrc) requiredSet.add(currentAudioSrc);

        const map = prefetchedTransAudiosRef.current;
        const loading = prefetchLoadingUrlsRef.current;

        // 确保 window.prefetchedTransAudios 始终指向同一个 Map，供现有播放逻辑复用
        if (!window.prefetchedTransAudios || window.prefetchedTransAudios !== map) {
            window.prefetchedTransAudios = map;
        }

        const finalizeLoading = (url: string) => {
            if (loading.has(url)) {
                loading.delete(url);
                prefetchInFlightRef.current = Math.max(0, prefetchInFlightRef.current - 1);
            }
        };

        const ensurePrefetched = (url: string) => {
            if (!url) return;

            // LRU：命中则把它挪到 Map 尾部
            if (map.has(url)) {
                const existing = map.get(url);
                if (existing) {
                    map.delete(url);
                    map.set(url, existing);
                }
                return;
            }
            if (loading.has(url)) return;
            if (prefetchInFlightRef.current >= PREFETCH_MAX_INFLIGHT) return;

            loading.add(url);
            prefetchInFlightRef.current += 1;

            const audio = new Audio();
            audio.preload = 'auto';
            audio.src = url;

            // 先写入缓存，方便立刻复用（即使还没完全 ready）
            map.set(url, audio);

            const onReady = () => finalizeLoading(url);
            const onError = () => finalizeLoading(url);
            // 用 canplay 即可认为“已具备播放所需数据”，比 canplaythrough 更早释放并发占用
            audio.addEventListener('canplay', onReady, { once: true });
            audio.addEventListener('error', onError, { once: true });

            // 超时兜底：避免网络异常导致 inFlight 永久占用
            window.setTimeout(() => {
                finalizeLoading(url);
            }, 10_000);

            // 开始加载（低优先级；不阻塞主流程）
            try {
                audio.load();
            } catch (e) {
                finalizeLoading(url);
            }
        };

        // 预取窗口内的 URL（按顺序，直到并发上限）
        for (const url of requiredUrls) {
            if (prefetchInFlightRef.current >= PREFETCH_MAX_INFLIGHT) break;
            ensurePrefetched(url);
        }

        // 缓存淘汰：优先删除不在 requiredSet 的最旧条目
        if (map.size > PREFETCH_CACHE_LIMIT) {
            for (const key of map.keys()) {
                if (map.size <= PREFETCH_CACHE_LIMIT) break;
                if (requiredSet.has(key)) continue;
                if (loading.has(key)) continue;
                const audio = map.get(key);
                map.delete(key);
                // 尝试释放资源
                if (audio) {
                    try {
                        audio.pause();
                        audio.src = '';
                        audio.load();
                    } catch {
                        // ignore
                    }
                }
            }
        }
        // 兜底：仍然超限则直接按最旧删除
        while (map.size > PREFETCH_CACHE_LIMIT) {
            const firstKey = map.keys().next().value as string | undefined;
            if (!firstKey) break;
            if (loading.has(firstKey) || requiredSet.has(firstKey)) {
                // 如果最老的是必需项，跳过它（避免死循环）
                break;
            }
            const audio = map.get(firstKey);
            map.delete(firstKey);
            if (audio) {
                try {
                    audio.pause();
                    audio.src = '';
                    audio.load();
                } catch {
                    // ignore
                }
            }
        }
    }, []);

    // 播放过程中动态维护预取窗口（timeupdate/seeked/play 触发；内部已节流）
    useEffect(() => {
        const video = sourceVideoRef.current;
        if (!video) return;

        const handleTick = () => {
            maintainPrefetchWindow(video.currentTime || 0);
        };

        video.addEventListener('timeupdate', handleTick);
        video.addEventListener('seeked', handleTick);
        video.addEventListener('play', handleTick);

        // 初始预取：以当前时间为基准
        handleTick();

        return () => {
            video.removeEventListener('timeupdate', handleTick);
            video.removeEventListener('seeked', handleTick);
            video.removeEventListener('play', handleTick);
        };
    }, [videoData?.id, maintainPrefetchWindow]);

    // 使用 ref 存储最新的 playTransAudioByTime 函数引用，避免闭包问题
    const playTransAudioByTimeRef = useRef<((currentTime: number, options?: { forceReplay?: boolean }) => void) | null>(null);
    const safeStopTransAudioRef = useRef<(() => void) | null>(null);
    
    // 根据时间戳播放翻译音频（优化版本：使用预处理数据和二分查找）
    const playTransAudioByTime = useCallback((currentTime: number, options?: { forceReplay?: boolean }) => {
        const forceReplay = options?.forceReplay || false;

        // 连续主缓冲已接管正常播放链路，逐句 mp3 仅保留给强制单句预览。
        if (translatedTrackPlayerRef.current && !forceReplay) {
            return;
        }
        
        // --- 新增：识别 Seek 跳变 ---
        const lastVideoTime = lastAudioCheckVideoTimeRef.current;
        const timeDiff = Math.abs(currentTime - lastVideoTime);
        const isSeekJump = timeDiff > 0.5; // 如果跳转超过 0.5s，视为主动 Seek
        lastAudioCheckVideoTimeRef.current = currentTime;
        
        // --- 调试监控已移除 ---
        
        // 如果是点击台词导致的跳转，不自动播放音频
        if (isSubtitleClickSeekRef.current && !forceReplay) {
            return;
        }
        
        // 如果视频没有在播放，不自动播放翻译音频（除非是强制重播）
        // 用 ref 兜底：刚点播放时 isPlaying 可能尚未更新，以视频实际播放状态为准，避免“暂停→点台词→点播放”播不出当前句
        const videoActuallyPlaying = !!(sourceVideoRef.current && !sourceVideoRef.current.paused);
        if (!isPlaying && !forceReplay && !videoActuallyPlaying) {
            return;
        }
        
        // 如果是强制重播，跳过某些限制检查
        if (forceReplay) {
            // 强制重播模式：跳过部分限制检查
        } else {
            // 如果正在播放台词（左侧或右侧），不自动播放翻译音频
            if (subtitlePlayEndTime !== null) {
                return;
            }

            // 如果刚刚结束台词播放，不立即播放翻译音频
            if (justFinishedSubtitlePlay) {
                return;
            }
        }

        if (!videoData || !videoData.subtitles) {
            return;
        }

        // 频率限制：每100ms最多检查一次（减少计算）
        // 但如果是强制重播，跳过频率限制
        if (!forceReplay && currentTime - lastAudioCheckTimeRef.current < translatedAudioTiming.detectIntervalSec) {
            return;
        }
        if (!forceReplay) {
            lastAudioCheckTimeRef.current = currentTime;
        }

        const matchTime = computeSubtitleMatchTime(currentTime, translatedAudioTiming);

        // 使用预处理的数据进行二分查找（优化性能）
        // 注意：processedSubtitlesRef.current 通过 useMemo 和 useEffect 自动与 targetText 同步更新
        // 确保二分查找使用的时间戳与右侧台词编辑区的时间戳保持一致
        const processed = processedSubtitlesRef.current;
        if (processed.length === 0) {
            return;
        }

        // 二分查找：找到第一个开始时间 <= currentTime 的字幕
        let left = 0;
        let right = processed.length - 1;
        let currentSubtitle: Task.SubtitlesLegacy | null = null;

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            const item = processed[mid];

            // 使用 < endTime 而不是 <= endTime，避免在边界处同时匹配两个字幕
            // startTime - 0.05 容错：配合 togglePlay 抢跑（actualTime + 0.1）时稳定命中当前句
            if (matchTime >= item.startTime - 0.05 && matchTime < item.endTime) {
                // 找到匹配的字幕
                currentSubtitle = item.subtitle;
                break;
            } else if (matchTime < item.startTime - 0.05) {
                // 当前时间在字幕开始时间之前，向左查找
                right = mid - 1;
            } else {
                // 当前时间在字幕结束时间之后，向右查找
                left = mid + 1;
            }
        }

        // 如果二分查找没找到，可能是因为字幕之间有重叠或间隙，使用线性查找作为兜底
        if (!currentSubtitle) {
            for (const item of processed) {
                // 使用 < endTime 而不是 <= endTime，避免在边界处同时匹配两个字幕；startTime - 0.05 容错配合抢跑
                if (matchTime >= item.startTime - 0.05 && matchTime < item.endTime) {
                    currentSubtitle = item.subtitle;
                    break;
                }
                // 如果已经超过当前字幕的结束时间，且下一个字幕的开始时间更晚，可以提前退出
                if (matchTime < item.startTime - 0.05) {
                    break;
                }
            }
        }

        const nextSubtitleWindow = getTranslatedSubtitleWindow(currentSubtitle);

        // 如果上一句刚自然播放结束，且时间仍停留在同一句范围内，不要重复拉起同一句
        if (
            currentSubtitle &&
            currentSubtitle.id === lastFinishedTransSubtitleIdRef.current &&
            !forceReplay &&
            !isSeekJump
        ) {
            return;
        }

        const currentPlayingWindow = currentPlayingSubtitleWindowRef.current;
        const isCurrentAudioActive = !!(
            transAudioRef.current &&
            !transAudioRef.current.paused &&
            currentPlayingSubtitleIdRef.current !== null
        );

        // 切句策略：
        // 1. 有时间重叠时，最新命中的句子立即抢占播放；
        // 2. 无时间重叠时，让当前句自然收尾，避免尾音被硬切。
        if (
            currentSubtitle &&
            currentPlayingSubtitleIdRef.current !== null &&
            currentPlayingSubtitleIdRef.current !== currentSubtitle.id &&
            nextSubtitleWindow
        ) {
            const hasTimeOverlap = !!(
                currentPlayingWindow &&
                nextSubtitleWindow.startTime < currentPlayingWindow.endTime &&
                nextSubtitleWindow.endTime > currentPlayingWindow.startTime
            );

            if (!forceReplay && !isSeekJump && isCurrentAudioActive && !hasTimeOverlap) {
                return;
            }

            if (safeStopTransAudioRef.current) {
                safeStopTransAudioRef.current();
            }
        }

        // 状态控制：如果已在播放当前字幕音频，直接跳过
        // 不再因为音频暂停/结束自动重新播放，避免在同一句时间范围内循环播放
        // 但如果是强制重播（如用户点击时间轴）或检测到时间跳变（Seek），则允许重新播放
        if (
            currentSubtitle && 
            currentSubtitle.id === currentPlayingSubtitleIdRef.current && 
            !forceReplay && 
            !isSeekJump // 关键：如果是跳变，不拦截
        ) {
            // 拦截：ID 相同（同一句台词），触发拦截退出
            return;
        }
        
        // 如果是强制重播或检测到时间跳变，且ID相同，清除当前播放状态，允许重新播放
        if ((forceReplay || isSeekJump) && currentSubtitle && currentSubtitle.id === currentPlayingSubtitleIdRef.current) {
            // 强制重播或时间跳变：清除当前播放状态，允许重新播放同一句台词
            // 停止当前音频
            if (transAudioRef.current && !transAudioRef.current.paused) {
                transAudioRef.current.pause();
                transAudioRef.current.currentTime = 0;
            }
            // 清除状态
            setCurrentTransAudio(null);
            currentPlayingSubtitleIdRef.current = null;
            currentPlayingSubtitleWindowRef.current = null;
        }

        // 无匹配字幕处理逻辑
        // 需求：只在刚进入某个时间戳时决定播放哪句音频，不对 end 时间戳做约束，
        //      即便 currentTime 处于两句台词之间的空白区域，也允许上一句音频自然播完
        //      但如果用户主动跳转（Seek 或 Force），即使没找到新字幕，也要停止旧的音频
        if (!currentSubtitle) {
            // 拦截：未找到匹配的字幕
            
            // --- 新增：清理逻辑 ---
            // 如果是用户主动点击(forceReplay)或大跨度跳转(isSeekJump)，
            // 说明用户明确想改变位置。即使当前位置没台词，也必须打断之前的声音。
            if (forceReplay || isSeekJump) {
                // 主动跳转到无台词区域，停止残留台词音频
                
                // 1. 停止原生 Audio 实例
                if (transAudioRef.current && !transAudioRef.current.paused) {
                    transAudioRef.current.pause();
                    transAudioRef.current.currentTime = 0;
                    setCurrentTransAudio(null);
                }
                
                // 2. 清除当前播放 ID 记录
                currentPlayingSubtitleIdRef.current = null;
                
                // 3. 停止 AudioManager (防止某些场景下 audioManager 在响)
                if (safeStopTransAudioRef.current) {
                    safeStopTransAudioRef.current();
                }
            }
            return;
        }

        const activeSubtitle = currentSubtitle;

        if (!activeSubtitle.trans_audio) {
            // 如果没有翻译音频，显示错误提示
            console.error(`字幕ID ${activeSubtitle.id} 缺少目标台词音频数据`);
            if (safeStopTransAudioRef.current) {
                safeStopTransAudioRef.current();
            }
            currentPlayingSubtitleIdRef.current = null;
            return;
        }

        // 构建翻译音频URL（支持绝对路径和相对路径）
        // trans_audio 使用内容哈希命名，可以使用强缓存
        const transAudioUrl = processTransAudioUrl(activeSubtitle.trans_audio);
        
        // 如果音频URL没有变化，不需要重新播放（但可能ID不同，需要更新状态）
        // 但如果是强制重播或检测到时间跳变，允许重新播放
        if (!forceReplay && !isSeekJump && currentTransAudio === transAudioUrl && currentPlayingSubtitleIdRef.current === activeSubtitle.id) {
            // 拦截：音频 URL 相同且 ID 相同，触发拦截退出
            return;
        }
        
        // 如果是强制重播或检测到时间跳变，且URL和ID都相同，清除状态以允许重新播放
        if ((forceReplay || isSeekJump) && currentTransAudio === transAudioUrl && currentPlayingSubtitleIdRef.current === activeSubtitle.id) {
            // 强制重播或时间跳变：URL和ID相同，清除状态以允许重新播放
            // 停止当前音频
            if (transAudioRef.current && !transAudioRef.current.paused) {
                transAudioRef.current.pause();
                transAudioRef.current.currentTime = 0;
            }
            // 清除状态
            setCurrentTransAudio(null);
            currentPlayingSubtitleIdRef.current = null;
        }
        
        // 通过所有拦截检查，准备播放翻译音频

        // 在播放翻译音频之前，先停止原人声音频
        if (vocalsAudioRef.current) {
            vocalsAudioRef.current.pause();
        }
        if (vocalsHlsRef.current) {
            vocalsHlsRef.current.destroy();
            vocalsHlsRef.current = null;
        }

        // 播放新音频：先停掉之前的
        if (currentPlayingSubtitleIdRef.current !== null) {
            if (safeStopTransAudioRef.current) {
                safeStopTransAudioRef.current();
            }
        }
        lastFinishedTransSubtitleIdRef.current = null;
        currentPlayingSubtitleIdRef.current = activeSubtitle.id;
        if (nextSubtitleWindow) {
            currentPlayingSubtitleWindowRef.current = {
                id: activeSubtitle.id,
                ...nextSubtitleWindow
            };
        } else {
            currentPlayingSubtitleWindowRef.current = null;
        }

        // 获取targetText中的最新数据（用于计算时间戳）
        // 注意：直接使用targetText而不是缓存的targetTextMapRef，确保使用右侧台词编辑区的最新时间戳
        const currentSubtitleId = activeSubtitle.id;
        const targetData = currentSubtitleId ? targetText.find(item => item.id === currentSubtitleId) : undefined;

        // 创建或更新播放音频元素（播放实例与预取缓存解耦，避免节点/事件泄漏）
        if (!transAudioRef.current) {
            transAudioRef.current = new Audio();
            transAudioRef.current.preload = 'auto';
        }

        const transAudio = transAudioRef.current;
        if (!transAudio) {
            console.error('无法创建音频元素');
            return;
        }
        
        // 设置音频源
        if (transAudio.src !== transAudioUrl) {
            transAudio.src = transAudioUrl;
        }

        // 获取语速设置
        const audioSpeed = activeSubtitle.audio_speed || 1.0;

        // 设置音频属性
        transAudio.volume = 1.0;
        // 使用台词数据中的audio_speed值
        transAudio.playbackRate = audioSpeed;
        
        // 计算时间偏移：当前时间 - 台词开始时间 = 偏移量
        // 优先使用targetText中的最新时间戳（如果存在）
        let startTime: number;
        let endTime: number;
        
        if (nextSubtitleWindow) {
            startTime = nextSubtitleWindow.startTime;
            endTime = nextSubtitleWindow.endTime;
        } else {
            const resolvedWindow = resolveSubtitleTimeWindow(activeSubtitle, targetData);
            startTime = resolvedWindow.startTime;
            endTime = resolvedWindow.endTime;
        }
        
        // 原始偏移：视频当前时间相对句首的偏移
        const rawOffset = computeAudioSeekOffset(currentTime, startTime, translatedAudioTiming);
        // 带阈值：仅当 forceReplay 且靠近句首（<0.3s）时强制从 0 播防吞音；否则按偏移播，保证点台词中间时音画同步
        const timeOffset =
            forceReplay && rawOffset < 0.3 ? 0 : rawOffset;

        // 确保时间偏移在合理范围内
        const maxOffset = endTime - startTime;
        const safeTimeOffset = Math.max(0, Math.min(timeOffset, maxOffset));

        // 优化的音频播放逻辑，减少延迟
        // 保存最新的时间戳用于闭包中
        const latestStartTime = startTime;
        const latestEndTime = endTime;
        
        const playAudioWhenReady = () => {
            let hasPlayed = false; // 防止重复播放的标志
            const playingSubtitleId = activeSubtitle.id;
            
            const playAudio = () => {
                if (hasPlayed) return; // 如果已经播放过，直接返回
                hasPlayed = true;
                
                // 处理播放成功的回调
                const handlePlaySuccess = () => {
                    // 翻译音频从偏移位置开始播放
                    setCurrentTransAudio(transAudioUrl);
                    
                    // 添加音频播放状态监控，基于实际音频长度
                    // 优化：使用ended事件监听，确保音频完整播放
                    const checkAudioProgress = () => {
                        if (!transAudio || transAudio.paused) {
                            return;
                        }
                        
                        const currentAudioTime = transAudio.currentTime;
                        const audioDuration = transAudio.duration;
                        
                        // 检查音频是否真正播放完成（基于实际音频长度）
                        if (audioDuration > 0 && currentAudioTime >= audioDuration - 0.1) {
                            // 翻译音频播放完成
                            setCurrentTransAudio(null);
                            currentPlayingSubtitleIdRef.current = null;
                            currentPlayingSubtitleWindowRef.current = null;
                            lastFinishedTransSubtitleIdRef.current = playingSubtitleId;
                            if (
                                sourceVideoRef.current &&
                                !sourceVideoRef.current.paused &&
                                !isSubtitleClickSeekRef.current &&
                                playTransAudioByTimeRef.current
                            ) {
                                setTimeout(() => {
                                    playTransAudioByTimeRef.current?.(sourceVideoRef.current?.currentTime || 0);
                                }, 0);
                            }
                            return;
                        }
                        
                        // 继续监控（减少监控频率到200ms，降低性能开销）
                        setTimeout(checkAudioProgress, 200);
                    };
                    
                    // 使用ended事件确保音频完整播放（更可靠）
                    const handleAudioEnded = () => {
                        // 翻译音频播放完成（ended事件）
                        setCurrentTransAudio(null);
                        currentPlayingSubtitleIdRef.current = null;
                        currentPlayingSubtitleWindowRef.current = null;
                        lastFinishedTransSubtitleIdRef.current = playingSubtitleId;
                        if (
                            sourceVideoRef.current &&
                            !sourceVideoRef.current.paused &&
                            !isSubtitleClickSeekRef.current &&
                            playTransAudioByTimeRef.current
                        ) {
                            setTimeout(() => {
                                playTransAudioByTimeRef.current?.(sourceVideoRef.current?.currentTime || 0);
                            }, 0);
                        }
                        transAudio.removeEventListener('ended', handleAudioEnded);
                    };
                    transAudio.addEventListener('ended', handleAudioEnded);
                    
                    // 开始监控音频播放进度
                    setTimeout(checkAudioProgress, 200);
                };
                
                // 处理播放失败的回调
                const handlePlayError = (error: any) => {
                    // 如果是 AbortError，说明播放被 pause 中断，尝试重试一次
                    if (error.name === 'AbortError') {
                        // 翻译音频播放被中断（AbortError），等待后重试
                        // 等待一小段时间后重试
                        setTimeout(() => {
                            // 确保音频已停止
                            if (!transAudio.paused) {
                                transAudio.pause();
                            }
                            // 再次尝试播放
                            setTimeout(() => {
                                transAudio.play().then(() => {
                                    handlePlaySuccess();
                                }).catch((retryError: any) => {
                                    console.error('翻译音频播放重试失败:', retryError);
                                    // 播放失败时清除状态
                                    currentPlayingSubtitleIdRef.current = null;
                                    setCurrentTransAudio(null);
                                });
                            }, 20);
                        }, 30);
                    } else {
                        console.error('翻译音频播放失败:', error);
                        // 播放失败时清除状态
                        currentPlayingSubtitleIdRef.current = null;
                        setCurrentTransAudio(null);
                    }
                };
                
                // --- 播放指令保护：确保在 pause 之后才 play，避免 AbortError ---
                // 确保播放速率为台词数据中的audio_speed值
                transAudio.playbackRate = audioSpeed;
                
                // 从偏移位置开始播放，确保台词与画面同步
                transAudio.currentTime = safeTimeOffset;
                
                // 确保音频已完全停止，避免与之前的 pause 指令冲突
                if (!transAudio.paused) {
                    transAudio.pause();
                }

                Promise.resolve().then(() => {
                    transAudio.play().then(() => {
                        handlePlaySuccess();
                    }).catch(handlePlayError);
                });
            };
            
            // 优化：检查是否为预取音频，如果是则直接播放
            const prefetchedMap = window.prefetchedTransAudios || prefetchedTransAudiosRef.current;
            const isPrefetched = !!prefetchedMap && prefetchedMap.has(transAudioUrl);
            
            if (isPrefetched && transAudio.readyState >= 3) { // HAVE_FUTURE_DATA - 预取音频通常已完全加载
                // 预取音频已就绪，直接播放
                playAudio();
            } else if (transAudio.readyState >= 1) { // HAVE_METADATA - 有元数据就可以尝试播放
                playAudio();
            } else {
                // 等待音频加载，但减少超时时间
                const handleCanPlay = () => {
                    transAudio.removeEventListener('canplay', handleCanPlay);
                    playAudio();
                };
                transAudio.addEventListener('canplay', handleCanPlay);
                
                // 超时保护，如果1秒内没有加载完成，强制播放
                setTimeout(() => {
                    transAudio.removeEventListener('canplay', handleCanPlay);
                    playAudio();
                }, 1000); // 从3秒减少到1秒
            }
        };

        playAudioWhenReady();
    }, [currentTransAudio, isPlaying, justFinishedSubtitlePlay, subtitlePlayEndTime, targetText, translatedAudioTiming, videoData]);

    // 更新 ref 以存储最新的函数引用（每次渲染都更新，打破闭包陷阱）
    // 关键：不使用依赖数组，确保每次组件渲染时都更新 ref，这样即使事件监听器是旧的，它指向的 ref 内容也是最新的
    useEffect(() => {
        playTransAudioByTimeRef.current = playTransAudioByTime;
    });

    // 使用逐帧脉冲驱动翻译音频自动切句（胶片帧驱动），避免仅依赖 timeupdate 导致的检测滞后
    useEffect(() => {
        const video = sourceVideoRef.current;
        if (!video) return;

        const tick = ({ mediaTime }: { mediaTime: number }) => {
            if (timelineEditorRef.current?.activeTrack !== 'translated') return;
            playTransAudioByTimeRef.current?.(mediaTime);
        };

        const pulse = createVideoFramePulse(video, tick);

        const handlePlay = () => pulse.start();
        const handlePause = () => pulse.stop();
        const handleEnded = () => pulse.stop();
        const handleSeeked = () => tick({ mediaTime: video.currentTime || 0 });

        if (!video.paused) {
            pulse.start();
        }

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('seeked', handleSeeked);

        return () => {
            pulse.stop();
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('ended', handleEnded);
            video.removeEventListener('seeked', handleSeeked);
        };
    }, [videoData?.id]);

    // 左右画面同步与 UI 播放时钟：rvfc 留在 MediaPlaybackManager 内完成
    useEffect(() => {
        playbackManagerRef.current?.destroy();
        playbackManagerRef.current = new MediaPlaybackManager({
            getSourceVideo: () => sourceVideoRef.current,
            getSubtitleOverlayPlayer: () => subtitleOverlayPlayerRef.current,
            // 用左侧视频 paused/ended 兜底；HLS 片尾可能仍标记为「未 pause」
            getIsPlaying: () => {
                const v = sourceVideoRef.current;
                if (!v) return false;
                if (v.ended || playbackFinishedRef.current) return false;
                const dur = v.duration;
                if (Number.isFinite(dur) && dur > 0 && v.currentTime >= dur - 0.03) {
                    return false;
                }
                return !v.paused;
            },
            getSubtitlePlayEndTime: () => subtitlePlayEndTimeRef.current,
            driftThresholdSec: VIDEO_SYNC_DRIFT_THRESHOLD_SEC,
            uiUpdateIntervalMs: VIDEO_SYNC_UI_UPDATE_INTERVAL_MS,
            setPlaybackUiTime,
        });
        playbackManagerRef.current.attach();

        return () => {
            playbackManagerRef.current?.destroy();
            playbackManagerRef.current = null;
        };
    }, [videoData?.id, setPlaybackUiTime]);

    const stopTransAudio = () => {
        if (transAudioRef.current) {
            transAudioRef.current.pause();
            transAudioRef.current.currentTime = 0;
            setCurrentTransAudio(null);
            currentPlayingSubtitleIdRef.current = null;
            currentPlayingSubtitleWindowRef.current = null;
            // 翻译音频已停止
        }
    };

    const stopAllTimelineAudio = useCallback(() => {
        translatedTrackPlayerRef.current?.pause();
        stopTransAudio();
        stopOriginAudio();
        stopCurrentPlayingInstance();
        backgroundAudioRef.current?.pause();
        if (vocalsAudioRef.current) {
            vocalsAudioRef.current.pause();
            vocalsAudioRef.current.currentTime = 0;
        }
    }, [stopCurrentPlayingInstance]);

    const handleVideoEnded = useCallback(() => {
        if (playbackFinishedRef.current) return;
        if (subtitlePlayEndTimeRef.current !== null) return;

        const video = sourceVideoRef.current;
        if (!video) return;

        playbackFinishedRef.current = true;

        const metaDur =
            Number.isFinite(video.duration) && video.duration > 0 ? video.duration : duration;
        const endTime =
            metaDur > 0
                ? Math.min(metaDur, Math.max(0, video.currentTime))
                : Math.max(0, video.currentTime);

        try {
            video.pause();
        } catch {
            /* ignore */
        }
        if (metaDur > 0) {
            try {
                video.currentTime = Math.max(0, metaDur - 0.001);
            } catch {
                /* ignore */
            }
        }

        if (subtitleOverlayPlayerRef.current) {
            try {
                subtitleOverlayPlayerRef.current.pause();
            } catch {
                /* ignore */
            }
            try {
                playbackManagerRef.current?.notifyRightSeekStarted();
                subtitleOverlayPlayerRef.current.seekToTime(endTime);
            } catch {
                /* ignore */
            }
        }

        stopAllTimelineAudio();
        setSubtitlePlayEndTime(null);
        audioPlayRequestedRef.current = false;
        setIsPlaying(false);
        usePlaybackStore.getState().setIsPlaying(false);
        syncPlaybackChromeToTime(endTime);
    }, [duration, stopAllTimelineAudio, syncPlaybackChromeToTime]);

    handleVideoEndedRef.current = handleVideoEnded;

    // 执行目标台词播放逻辑（字幕行「播放」按钮）
    const executePlayTargetSubtitle = async (timeRange: string, subtitleId?: number) => {
        // 全局唯一播放实例管理：先停止当前播放实例
        stopCurrentPlayingInstance();
        pauseTranslatedTrackBuffer();
        
        // 若当前整体处于播放状态，先统一暂停，避免状态冲突
        if (isPlaying) {
            if (sourceVideoRef.current) sourceVideoRef.current.pause();
            if (subtitleOverlayPlayerRef.current) try { subtitleOverlayPlayerRef.current.pause(); } catch {}
            if (backgroundAudioRef.current) backgroundAudioRef.current.pause();
            stopTransAudio();
            stopOriginAudio();
            // 停止原人声音频
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
            }
            if (vocalsHlsRef.current) {
                vocalsHlsRef.current.destroy();
                vocalsHlsRef.current = null;
            }
            setIsPlaying(false);
        }
        // 在播放前同步待更新的时间轴数据
        if (timelineEditorRef.current && pendingTimelineUpdates.size > 0) {
            timelineEditorRef.current.syncPendingUpdatesToVideoData(pendingTimelineUpdates);
        }
        // 将时间轴刻度线与视图定位到该台词开始时间
        try {
            const [startTimeStr] = timeRange.split(' → ');
            const startTime = parseTimeToSeconds(startTimeStr);
            if (timelineEditorRef.current) {
                timelineEditorRef.current.currentTime = startTime;
                timelineEditorRef.current.updatePlayheadPosition();
                if (typeof (timelineEditorRef.current as any).scrollToTime === 'function') {
                    (timelineEditorRef.current as any).scrollToTime(startTime);
                }
            }
            // 严格等待左右视频都seek到位
            if (sourceVideoRef.current) {
                sourceVideoRef.current.currentTime = startTime;
                sourceVideoRef.current.pause();
            }
            if (subtitleOverlayPlayerRef.current) {
                playbackManagerRef.current?.notifyRightSeekStarted();
                try { subtitleOverlayPlayerRef.current.seekToTime(startTime); } catch {}
                try { subtitleOverlayPlayerRef.current.pause(); } catch {}
            }
            await waitBothSeeked(startTime);
        } catch {}
        
        // 目标台词播放：开始播放目标台词
        
        // 如果正在播放台词，先停止当前播放，然后开始新的播放
        if (subtitlePlayEndTime !== null) {
            // 目标台词播放：检测到正在播放其他台词，先停止当前播放
            
            // 停止所有正在播放的媒体
            if (sourceVideoRef.current) {
                sourceVideoRef.current.pause();
            }
            if (subtitleOverlayPlayerRef.current) {
                try { subtitleOverlayPlayerRef.current.pause(); } catch {}
            }
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
            }
            
            // 停止所有音频
            stopTransAudio();
            stopOriginAudio();
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
            }
            if (vocalsHlsRef.current) {
                vocalsHlsRef.current.destroy();
                vocalsHlsRef.current = null;
            }
            
            // 停止通过 AudioManager 播放的音频
            try {
                if (currentPlayingSubtitleIdRef.current !== null) {
                    const audioId = `target_audio_${currentPlayingSubtitleIdRef.current}`;
                    audioManager.stopSound(audioId);
                    audioManager.destroySound(audioId);
                }
            } catch (error) {
                // 停止 AudioManager 音频时出错
            }
            
            // 清除定时器
            if (subtitlePlayTimeoutRef.current) {
                clearTimeout(subtitlePlayTimeoutRef.current);
                subtitlePlayTimeoutRef.current = null;
            }
            
            // 清除播放状态
            setSubtitlePlayEndTime(null);
            setIsPlaying(false);
            setCurrentTransAudio(null);
            currentPlayingSubtitleIdRef.current = null;
            
            // 目标台词播放：已停止当前播放，准备播放新台词
        }
        
        if (!videoData || !videoData.subtitles) {
            // 没有字幕数据，无法播放目标台词音频
            return;
        }

        // 重置刚结束台词播放的标志
        setJustFinishedSubtitlePlay(false);

        // 停止原始台词音频播放
        stopOriginAudio();

        // 查找对应的字幕数据 - 优先使用字幕ID获取最新数据
        let matchingSubtitle: Subtitle | undefined;
        
        if (subtitleId) {
            // 使用字幕ID直接查找，确保获取最新数据
            matchingSubtitle = videoData.subtitles.find(subtitle => subtitle.id === subtitleId);
            // 目标台词播放：使用字幕ID查找
        } else {
            // 如果没有提供字幕ID，使用时间范围查找（兼容旧逻辑）
            matchingSubtitle = videoData.subtitles.find(subtitle => {
                // 尝试匹配原始时间范围
                const originTimeRange = `${subtitle.origin_start_time} → ${subtitle.origin_end_time}`;
                if (originTimeRange === timeRange) {
                    return true;
                }
                
                // 尝试匹配目标时间范围（post时间）
                const targetTimeRange = `${subtitle.post_start_time || subtitle.origin_start_time} → ${subtitle.post_end_time || subtitle.origin_end_time}`;
                if (targetTimeRange === timeRange) {
                    return true;
                }
                
                return false;
            });
            // 目标台词播放：使用时间范围查找
        }

        if (!matchingSubtitle || !matchingSubtitle.trans_audio) {
            console.error('未找到对应的目标台词音频:', {
                requestedTimeRange: timeRange,
                foundMatchingSubtitle: !!matchingSubtitle,
                hasTransAudio: matchingSubtitle ? !!matchingSubtitle.trans_audio : false,
                transAudioUrl: matchingSubtitle ? matchingSubtitle.trans_audio : null
            });
            // 可用的字幕数据已记录
            return;
        }

        // 使用最新的时间戳计算时间范围
        const latestStartTime = matchingSubtitle.post_start_time || matchingSubtitle.origin_start_time;
        const latestEndTime = matchingSubtitle.post_end_time || matchingSubtitle.origin_end_time;
        const startTime = parseTimeToSeconds(latestStartTime);
        const endTime = parseTimeToSeconds(latestEndTime);
        
        // 目标台词播放：使用最新时间戳
        
        // 清除之前的定时器
        if (subtitlePlayTimeoutRef.current) {
            clearTimeout(subtitlePlayTimeoutRef.current);
        }
        
        // 使用精确同步机制前，严格等待左右视频seek完成
        (async () => {
            try {
                if (sourceVideoRef.current) {
                    sourceVideoRef.current.currentTime = startTime;
                    sourceVideoRef.current.pause();
                }
                if (subtitleOverlayPlayerRef.current) {
                    playbackManagerRef.current?.notifyRightSeekStarted();
                    try { subtitleOverlayPlayerRef.current.seekToTime(startTime); } catch {}
                    try { subtitleOverlayPlayerRef.current.pause(); } catch {}
                }
                await waitBothSeeked(startTime);
            } catch {}
        })();
        // 使用精确同步机制
        const waitForVideosReadyAndPlay = async () => {
            // 目标台词播放：开始精确同步
            
            // 更新进度条显示
            const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
            if (progressBar && duration > 0) {
                const progressPercent = (startTime / duration) * 100;
                progressBar.style.setProperty('--progress', `${progressPercent}%`);
            }
            
            setPlaybackUiTime(startTime);
            
            // 使用精确同步启动
            await startPreciseSync(startTime);
            
            // 目标台词播放：精确同步完成，开始播放
            
            // 现在两个视频都精确同步了，真正同时开始播放
            // 目标台词播放：准备同时启动两个视频
            
            // 使用requestAnimationFrame确保在同一帧内启动
            await new Promise<void>((resolve) => {
                requestAnimationFrame(() => {
                    const playPromises: Promise<any>[] = [];
                    
                    if (sourceVideoRef.current) {
                        sourceVideoRef.current.muted = true;
                        playPromises.push(
                            sourceVideoRef.current.play().catch(error => {
                                console.error('目标台词播放：左侧视频播放失败:', error);
                            })
                        );
                    }
                    
                    if (subtitleOverlayPlayerRef.current) {
                        playPromises.push(
                            new Promise<void>((resolve) => {
                                subtitleOverlayPlayerRef.current!.play();
                                // 给右侧视频一点时间启动
                                setTimeout(() => resolve(), 10);
                            })
                        );
                    }
                    
                    // 等待两个视频都开始播放
                    Promise.all(playPromises).then(() => {
                        // 目标台词播放：两个视频已真正同时开始播放，毫秒级同步已启动
                        resolve();
                    });
                });
            });
            
            // 播放目标台词音频（trans_audio）- 使用 Howler.js 实现真正的音量放大
            if (!matchingSubtitle) {
                console.error('matchingSubtitle is undefined');
                return;
            }
            
            // 构建目标台词音频URL（支持绝对路径和相对路径）
            // trans_audio 使用内容哈希命名，可以使用强缓存
            const targetAudioUrl = processTransAudioUrl(matchingSubtitle.trans_audio);
            // 播放目标台词音频

            // 获取音量增益设置（优先使用音量映射表中的最新值）
            const latestVolume = volumeMap.get(matchingSubtitle.id);
            const volumeGainDb = latestVolume !== undefined ? latestVolume : (matchingSubtitle.volume_gain_db || 1.0);
            const audioSpeed = matchingSubtitle.audio_speed || 1.0;
            
            // 目标台词播放：使用 Howler.js 播放音频

            // 使用 AudioManager 播放音频
            const audioId = `target_audio_${matchingSubtitle.id}`;
            
            try {
                // 创建音频实例（参考测试案例）
                // 使用台词数据中的audio_speed值
                audioManager.createSound(audioId, targetAudioUrl, {
                    volume: 1.0, // 基础音量，实际音量通过 gain 控制
                    rate: audioSpeed, // 使用台词数据中的audio_speed值
                    preload: true,
                    html5: true // 强制使用 HTML5 模式
                });

                // ================== 【必须加】全局变量赋值：立即赋值，不要等待 await！ ==================
                // 🔥 创建后，立马赋值给全局变量！不要等待 await！
                // 这样即使在 await 期间离开页面，清理函数也能找到这个音频 ID
                GLOBAL_PLAYING_AUDIO_ID = audioId;
                console.log('全局变量已赋值:', GLOBAL_PLAYING_AUDIO_ID);
                // ================== 【必须加】结束 ==================

                // 播放音频并设置音量增益
                await audioManager.playSound(audioId, volumeGainDb, 0);
                
                // 目标台词音频播放成功（Howler.js）
                
                // 设置当前播放的音频引用，用于后续控制
                setCurrentTransAudio(targetAudioUrl);
                currentPlayingSubtitleIdRef.current = matchingSubtitle.id;
                
                // 全局唯一播放实例管理：记录新的播放实例
                currentPlayingInstanceRef.current = {
                    type: 'target',
                    audioId: audioId,
                    subtitleId: matchingSubtitle.id,
                    timeRange: timeRange
                };
                // 全局播放管理：记录新的翻译后台词播放实例
                
            } catch (error) {
                console.error('目标台词音频播放失败（Howler.js）:', error);
                // 如果 Howler.js 播放失败，回退到原生 Audio
                // 回退到原生 Audio 播放
                
                // 创建或更新音频元素
                if (!transAudioRef.current) {
                    transAudioRef.current = new Audio();
                }

                const targetAudio = transAudioRef.current;
                
                // 设置音频源
                if (targetAudio.src !== targetAudioUrl) {
                    targetAudio.src = targetAudioUrl;
                }

                // 设置音频属性
                targetAudio.currentTime = 0;
                // 使用台词数据中的audio_speed值
                targetAudio.playbackRate = audioSpeed;
                // 注意：原生 Audio 的 volume 不能超过 1.0
                targetAudio.volume = Math.min(1.0, volumeGainDb);
                
                // 全局唯一播放实例管理：记录新的播放实例（回退模式）
                currentPlayingInstanceRef.current = {
                    type: 'target',
                    subtitleId: matchingSubtitle.id,
                    timeRange: timeRange
                };
                // 全局播放管理：记录新的翻译后台词播放实例（回退模式）
                // 目标台词播放：回退到原生 Audio

            // 等待音频加载完成后再播放
                // 等待音频加载完成后再播放（回退模式）
                const playTargetAudioWhenReady = () => {
                    let hasPlayed = false; // 防止重复播放的标志
                    
                    const playAudio = () => {
                        if (hasPlayed) return; // 如果已经播放过，直接返回
                        hasPlayed = true;
                        
                        // 在播放前重新设置语速和音量，确保使用最新值
                        if (!matchingSubtitle) return;
                        
                        // 使用台词数据中的audio_speed值
                        const currentAudioSpeed = matchingSubtitle.audio_speed || 1.0;
                        targetAudio.playbackRate = currentAudioSpeed;
                        
                        const currentLatestVolume = volumeMap.get(matchingSubtitle.id);
                        const currentVolumeGainDb = currentLatestVolume !== undefined ? currentLatestVolume : (matchingSubtitle.volume_gain_db || 1.0);
                        // 注意：原生 Audio 的 volume 不能超过 1.0
                        const currentFinalVolume = Math.min(1.0, Math.max(0, currentVolumeGainDb));
                        targetAudio.volume = currentFinalVolume;
                        
                        // 目标台词播放：播放前重新设置语速和音量（回退模式）
                        targetAudio.play().then(() => {
                            // 目标台词音频播放成功（回退模式）
                            setCurrentTransAudio(targetAudioUrl);
                            if (matchingSubtitle) {
                                currentPlayingSubtitleIdRef.current = matchingSubtitle.id;
                            }
                        }).catch(error => {
                            console.error('目标台词音频播放失败（回退模式）:', error);
                        });
                    };
                    
                    if (targetAudio.readyState >= 2) { // HAVE_CURRENT_DATA
                        playAudio();
                    } else {
                        // 等待音频加载
                        const handleCanPlay = () => {
                            targetAudio.removeEventListener('canplay', handleCanPlay);
                            playAudio();
                        };
                        targetAudio.addEventListener('canplay', handleCanPlay);
                        
                        // 超时保护，如果5秒内没有加载完成，强制播放
                        setTimeout(() => {
                            targetAudio.removeEventListener('canplay', handleCanPlay);
                            if (!hasPlayed) {
                                // 音频加载超时，强制播放（回退模式）
                                playAudio();
                            }
                        }, 5000);
                    }
                };
                
                playTargetAudioWhenReady();
            }
            
            // 播放目标台词时，暂停背景音，只播放台词
            // 播放目标台词：暂停背景音，只播放台词音频
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
            }
            
            // 目标台词播放：所有媒体已同时开始播放
        };
        
        // 开始等待并播放流程
        waitForVideosReadyAndPlay();
        
        // 标记为用户手动操作
        userInteractionRef.current = true;
        
        // 立即同步音频到开始时间
        syncAudioToVideo();
        
        // 设置播放状态
        setIsPlaying(true);
        
        // 设置结束时间，用于在时间更新时检查是否需要暂停
        setSubtitlePlayEndTime(endTime);
        
        // 目标台词播放：已设置结束时间
    };

    // 只播放背景音的函数
    const playBackgroundAudioOnly = () => {
        if (!videoData) {
            // videoData为空，无法播放背景音
            return;
        }

        // 判断背景音开关状态，如果关闭则不播放背景音
        if (!backgroundAudioEnabled) {
            // 背景音开关已关闭，跳过播放背景音
            return;
        }

        // 设置音频播放请求标记
        audioPlayRequestedRef.current = true;
        
        const videoTime = sourceVideoRef.current?.currentTime || 0;
        // console.log('播放背景音，视频时间:', videoTime);

        // 只播放背景音
        if (videoData.backgroud_audio_hls_url) {
            // backgroud_audio_hls_url 已经在 getTaskStatusApi 中处理过了，包含 /storage 前缀
            // 如果已经是完整URL或已包含代理前缀，直接使用；否则使用 processVideoUrl 处理
            const backgroundAudioUrl = videoData.backgroud_audio_hls_url.startsWith('http://') || videoData.backgroud_audio_hls_url.startsWith('https://') || videoData.backgroud_audio_hls_url.startsWith('/storage') || videoData.backgroud_audio_hls_url.startsWith('/media')
                ? videoData.backgroud_audio_hls_url
                : processVideoUrl(videoData.backgroud_audio_hls_url);
            if (backgroundAudioRef.current) {
                // 检查是否是HLS格式
                if (backgroundAudioUrl.includes('.m3u8')) {
                    // 对于HLS格式，我们需要使用HLS.js
                    import('hls.js').then((Hls) => {
                        if (Hls.default.isSupported()) {
                            // 在创建HLS实例前，再次检查是否应该继续（可能在异步过程中已被暂停）
                            if (!audioPlayRequestedRef.current) {
                                // 背景音HLS：播放请求已取消，跳过创建HLS实例
                                return;
                            }
                            // 清理之前的HLS实例
                            if (backgroundHlsRef.current) {
                                backgroundHlsRef.current.destroy();
                            }
                            const hls = new Hls.default({
                                enableWorker: true,
                                lowLatencyMode: true, // 启用低延迟模式
                            });
                            backgroundHlsRef.current = hls;
                            hls.loadSource(backgroundAudioUrl);
                            hls.attachMedia(backgroundAudioRef.current!);
                            hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                                // 在清单解析完成时，再次检查是否应该播放（可能在解析期间已被暂停）
                                if (!audioPlayRequestedRef.current) {
                                    // 背景音HLS清单解析完成，但播放请求已取消，跳过播放
                                    return;
                                }
                                // 再次检查HLS实例是否还存在（可能在解析期间已被销毁）
                                if (!backgroundHlsRef.current || backgroundHlsRef.current !== hls) {
                                    // 背景音HLS清单解析完成，但HLS实例已被销毁，跳过播放
                                    return;
                                }
                                // 立即同步到视频时间
                                backgroundAudioRef.current!.currentTime = videoTime;
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                backgroundAudioRef.current!.volume = 1.0;
                                backgroundAudioRef.current!.play().then(() => {
                                    // 再次检查音频就绪状态
                                    checkAudioReady();
                                }).catch(error => {
                                    // 静默处理播放失败，不输出错误日志
                                });
                            });
                            hls.on(Hls.default.Events.ERROR, (event, data) => {
                                // 静默处理HLS错误，不输出错误日志
                            });
                        } else {
                            console.error('浏览器不支持HLS.js');
                        }
                    }).catch(error => {
                        console.error('加载HLS.js失败:', error);
                    });
                } else {
                    // 非HLS格式，直接播放
                    if (backgroundAudioRef.current) {
                        backgroundAudioRef.current.src = backgroundAudioUrl;
                        backgroundAudioRef.current.addEventListener('canplaythrough', () => {
                            // 立即同步到视频时间
                            if (backgroundAudioRef.current) {
                                backgroundAudioRef.current.currentTime = videoTime;
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                if (audioPlayRequestedRef.current) {
                                    backgroundAudioRef.current.volume = 1.0;
                                    backgroundAudioRef.current.play().then(() => {
                                    }).catch(error => {
                                        // 静默处理播放失败
                                    });
                                }
                            }
                        });
                        backgroundAudioRef.current.load();
                    }
                }
            }
        }
    };

    // 优化的音频播放函数 - 立即同步时间并播放
    const playAudioTracksWithSync = () => {
        if (!videoData) {
            // videoData为空，无法播放音频
            return;
        }

        // 设置音频播放请求标记
        audioPlayRequestedRef.current = true;
        
        const videoTime = sourceVideoRef.current?.currentTime || 0;
        // 立即同步音频播放

        // 播放背景音（根据开关状态决定是否播放）
        if (videoData.backgroud_audio_hls_url && backgroundAudioEnabled) {
            // backgroud_audio_hls_url 已经在 getTaskStatusApi 中处理过了，包含 /storage 前缀
            // 如果已经是完整URL或已包含代理前缀，直接使用；否则使用 processVideoUrl 处理
            const backgroundAudioUrl = videoData.backgroud_audio_hls_url.startsWith('http://') || videoData.backgroud_audio_hls_url.startsWith('https://') || videoData.backgroud_audio_hls_url.startsWith('/storage') || videoData.backgroud_audio_hls_url.startsWith('/media')
                ? videoData.backgroud_audio_hls_url
                : processVideoUrl(videoData.backgroud_audio_hls_url);
            // 准备播放背景音
            if (backgroundAudioRef.current) {
                // 检查是否是HLS格式
                if (backgroundAudioUrl.includes('.m3u8')) {
                    // 检测到HLS格式的背景音，尝试使用HLS.js播放
                    // 对于HLS格式，我们需要使用HLS.js
                    import('hls.js').then((Hls) => {
                        if (Hls.default.isSupported()) {
                            // 清理之前的HLS实例
                            if (backgroundHlsRef.current) {
                                backgroundHlsRef.current.destroy();
                            }
                            const hls = new Hls.default({
                                enableWorker: true,
                                lowLatencyMode: true, // 启用低延迟模式
                            });
                            backgroundHlsRef.current = hls;
                            hls.loadSource(backgroundAudioUrl);
                            hls.attachMedia(backgroundAudioRef.current!);
                            hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                                // 背景音HLS清单解析完成
                                // 立即同步到视频时间
                                backgroundAudioRef.current!.currentTime = videoTime;
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                // 检查是否还在播放状态，避免在解析期间被暂停
                                if (audioPlayRequestedRef.current) {
                                    backgroundAudioRef.current!.volume = 1.0;
                                    backgroundAudioRef.current!.play().then(() => {
                                        // 背景音HLS播放成功，已同步到时间
                                        // 再次检查音频就绪状态
                                        checkAudioReady();
                                    }).catch(error => {
                                        console.error('背景音HLS播放失败:', error);
                                    });
                                }
                            });
                        } else {
                            // 浏览器不支持HLS，尝试直接播放
                            if (backgroundAudioRef.current) {
                                backgroundAudioRef.current.src = backgroundAudioUrl;
                                backgroundAudioRef.current.addEventListener('canplaythrough', () => {
                                    // 背景音加载完成
                                    // 立即同步到视频时间
                                    if (backgroundAudioRef.current) {
                                        backgroundAudioRef.current.currentTime = videoTime;
                                        // 更新音频就绪状态
                                        checkAudioReady();
                                        
                                        if (audioPlayRequestedRef.current) {
                                            backgroundAudioRef.current.volume = 1.0;
                                            backgroundAudioRef.current.play().then(() => {
                                                // 背景音播放成功，已同步到时间
                                            }).catch(error => {
                                                console.error('背景音播放失败:', error);
                                            });
                                        }
                                    }
                                });
                                backgroundAudioRef.current.load();
                            }
                        }
                    });
                } else {
                    // 非HLS格式，直接播放
                    if (backgroundAudioRef.current) {
                        backgroundAudioRef.current.src = backgroundAudioUrl;
                        backgroundAudioRef.current.addEventListener('canplaythrough', () => {
                            // 背景音加载完成
                            // 立即同步到视频时间
                            if (backgroundAudioRef.current) {
                                backgroundAudioRef.current.currentTime = videoTime;
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                if (audioPlayRequestedRef.current) {
                                    backgroundAudioRef.current.volume = 1.0;
                                    backgroundAudioRef.current.play().then(() => {
                                        // 背景音播放成功，已同步到时间
                                    }).catch(error => {
                                        console.error('背景音播放失败:', error);
                                    });
                                }
                            }
                        });
                        backgroundAudioRef.current.load();
                    }
                }
            }
        }

        // 注意：翻译语音现在通过时间戳动态播放，不再使用HLS流
        
        // 根据选中的轨道初始化原人声音频（如果选中"原视频"轨道）
        const currentActiveTrack = timelineEditorRef.current?.activeTrack || 'translated';
        if (currentActiveTrack === 'original' && videoData.vocals_hls_url) {
            initVocalsAudio();
        }
    };
    
    // 初始化原人声音频（vocals_hls_url）
    const initVocalsAudio = () => {
        if (!videoData || !videoData.vocals_hls_url) {
            // videoData为空或没有原人声音频，无法播放
            return;
        }

        // 在播放原人声音频之前，先停止翻译音频
        pauseTranslatedTrackBuffer();
        stopTransAudio();
        if (transAudioRef.current) {
            transAudioRef.current.pause();
            transAudioRef.current.currentTime = 0;
            setCurrentTransAudio(null);
        }

        const videoTime = sourceVideoRef.current?.currentTime || 0;
        // 构建原人声音频URL（接口已添加前缀，这里再做一次保险处理，兼容相对路径和绝对路径）
        const vocalsAudioUrl = processVideoUrl(videoData.vocals_hls_url as any);
        // 初始化原人声音频

        // 创建或获取原人声音频元素
        if (!vocalsAudioRef.current) {
            vocalsAudioRef.current = new Audio();
        }

        // 检查是否是HLS格式
        if (vocalsAudioUrl.includes('.m3u8')) {
            // 检测到HLS格式的原人声音频，尝试使用HLS.js播放
            import('hls.js').then((Hls) => {
                if (Hls.default.isSupported()) {
                    // 清理之前的HLS实例
                    if (vocalsHlsRef.current) {
                        vocalsHlsRef.current.destroy();
                    }
                    const hls = new Hls.default({
                        enableWorker: true,
                        lowLatencyMode: true,
                    });
                    vocalsHlsRef.current = hls;
                    hls.loadSource(vocalsAudioUrl);
                    hls.attachMedia(vocalsAudioRef.current!);
                    hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                        // 原人声音频HLS清单解析完成
                        // 立即同步到视频时间
                        vocalsAudioRef.current!.currentTime = videoTime;
                        
                        // 检查是否还在播放状态，避免在解析期间被暂停
                        const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                        if (audioPlayRequestedRef.current && isCurrentlyPlaying) {
                            vocalsAudioRef.current!.volume = 1.0;
                            vocalsAudioRef.current!.play().then(() => {
                                // 原人声音频HLS播放成功，已同步到时间
                            }).catch(error => {
                                console.error('原人声音频HLS播放失败:', error);
                            });
                        }
                    });
                    hls.on(Hls.default.Events.ERROR, (event, data) => {
                        console.error('原人声音频HLS错误:', data);
                    });
                } else {
                    // 浏览器不支持HLS，尝试直接播放
                    if (vocalsAudioRef.current) {
                        vocalsAudioRef.current.src = vocalsAudioUrl;
                        vocalsAudioRef.current.addEventListener('canplaythrough', () => {
                            // 原人声音频加载完成
                            if (vocalsAudioRef.current) {
                                vocalsAudioRef.current.currentTime = videoTime;
                                const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                                if (audioPlayRequestedRef.current && isCurrentlyPlaying) {
                                    vocalsAudioRef.current.volume = 1.0;
                                    vocalsAudioRef.current.play().then(() => {
                                        // 原人声音频播放成功，已同步到时间
                                    }).catch(error => {
                                        console.error('原人声音频播放失败:', error);
                                    });
                                }
                            }
                        });
                        vocalsAudioRef.current.load();
                    }
                }
            }).catch(error => {
                console.error('加载HLS.js失败:', error);
            });
        } else {
            // 非HLS格式，直接播放
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.src = vocalsAudioUrl;
                vocalsAudioRef.current.addEventListener('canplaythrough', () => {
                    if (vocalsAudioRef.current) {
                        vocalsAudioRef.current.currentTime = videoTime;
                        const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                        if (audioPlayRequestedRef.current && isCurrentlyPlaying) {
                            vocalsAudioRef.current.volume = 1.0;
                            vocalsAudioRef.current.play().then(() => {
                                // 原人声音频播放成功，已同步到时间
                            }).catch(error => {
                                console.error('原人声音频播放失败:', error);
                            });
                        }
                    }
                });
                vocalsAudioRef.current.load();
            }
        }
    };
    
    // 播放音频轨道
    const playAudioTracks = () => {
        if (!videoData) {
            // videoData为空，无法播放音频
            return;
        }

        // 获取当前激活的轨道
        const currentActiveTrack = timelineEditorRef.current?.activeTrack || 'translated';

        // 根据当前轨道，先停止其他轨道的音频
        if (currentActiveTrack === 'original') {
            // 切换到原视频轨道：停止翻译音频
            stopTransAudio();
            if (transAudioRef.current) {
                transAudioRef.current.pause();
                transAudioRef.current.currentTime = 0;
                setCurrentTransAudio(null);
            }
        } else if (currentActiveTrack === 'translated') {
            // 切换到翻译后轨道：停止原人声音频
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
            }
            if (vocalsHlsRef.current) {
                vocalsHlsRef.current.destroy();
                vocalsHlsRef.current = null;
            }
        }

        // 设置音频播放请求标记
        audioPlayRequestedRef.current = true;

        // 开始播放音频轨道

        // 播放背景音（根据开关状态决定是否播放）
        if (videoData.backgroud_audio_hls_url && backgroundAudioEnabled) {
            // backgroud_audio_hls_url 已经在 getTaskStatusApi 中处理过了，包含 /storage 前缀
            // 如果已经是完整URL或已包含代理前缀，直接使用；否则使用 processVideoUrl 处理
            const backgroundAudioUrl = videoData.backgroud_audio_hls_url.startsWith('http://') || videoData.backgroud_audio_hls_url.startsWith('https://') || videoData.backgroud_audio_hls_url.startsWith('/storage') || videoData.backgroud_audio_hls_url.startsWith('/media')
                ? videoData.backgroud_audio_hls_url
                : processVideoUrl(videoData.backgroud_audio_hls_url);
            // 准备播放背景音
            if (backgroundAudioRef.current) {
                // 检查是否是HLS格式
                if (backgroundAudioUrl.includes('.m3u8')) {
                    // 检测到HLS格式的背景音，尝试使用HLS.js播放
                    // 对于HLS格式，我们需要使用HLS.js
                    import('hls.js').then((Hls) => {
                        if (Hls.default.isSupported()) {
                            // 在创建HLS实例前，再次检查是否应该继续（可能在异步过程中已被暂停）
                            if (!audioPlayRequestedRef.current) {
                                // 背景音HLS：播放请求已取消，跳过创建HLS实例
                                return;
                            }
                            // 清理之前的HLS实例
                            if (backgroundHlsRef.current) {
                                backgroundHlsRef.current.destroy();
                            }
                            const hls = new Hls.default({
                                enableWorker: true,
                                lowLatencyMode: true, // 启用低延迟模式
                            });
                            backgroundHlsRef.current = hls;
                            hls.loadSource(backgroundAudioUrl);
                            hls.attachMedia(backgroundAudioRef.current!);
                            hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                                // 背景音HLS清单解析完成
                                // 在清单解析完成时，再次检查是否应该播放（可能在解析期间已被暂停）
                                if (!audioPlayRequestedRef.current) {
                                    // 背景音HLS清单解析完成，但播放请求已取消，跳过播放
                                    return;
                                }
                                // 再次检查HLS实例是否还存在（可能在解析期间已被销毁）
                                if (!backgroundHlsRef.current || backgroundHlsRef.current !== hls) {
                                    // 背景音HLS清单解析完成，但HLS实例已被销毁，跳过播放
                                    return;
                                }
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                // 场景3：音频轨道初始化播放 - 同步到视频时间
                                backgroundAudioRef.current!.currentTime = sourceVideoRef.current?.currentTime || 0;
                                backgroundAudioRef.current!.volume = 1.0;
                                backgroundAudioRef.current!.play().then(() => {
                                    // 背景音HLS播放成功
                                    // 再次检查音频就绪状态
                                    checkAudioReady();
                                }).catch(error => {
                                    console.error('背景音HLS播放失败:', error);
                                });
                            });
                            hls.on(Hls.default.Events.ERROR, (event, data) => {
                                console.error('背景音HLS错误:', data);
                            });
                        } else {
                            console.error('浏览器不支持HLS.js');
                        }
                    }).catch(error => {
                        console.error('加载HLS.js失败:', error);
                    });
                } else {
                    // 普通音频格式
                    backgroundAudioRef.current.src = backgroundAudioUrl;
                    
                    // 监听音频加载完成事件
                    backgroundAudioRef.current.addEventListener('canplaythrough', () => {
                        // 背景音加载完成
                        checkAudioReady();
                    });
                    
                    // 场景3：音频轨道初始化播放 - 同步到视频时间
                    backgroundAudioRef.current.currentTime = sourceVideoRef.current?.currentTime || 0;
                    backgroundAudioRef.current.volume = 1.0;
                    backgroundAudioRef.current.play().then(() => {
                        // 背景音播放成功
                        checkAudioReady();
                    }).catch(error => {
                        console.error('背景音播放失败:', error);
                    });
                }
                // 播放背景音
            } else {
                console.error('backgroundAudioRef.current为空');
            }
        } else {
            // 没有背景音URL
        }

        // 根据当前激活的轨道，初始化对应的人声音频
        if (currentActiveTrack === 'original' && videoData.vocals_hls_url) {
            // 原视频轨道：初始化原人声音频
            initVocalsAudio();
        }
        // 翻译后轨道的人声音频会在时间更新时自动播放（通过 playTransAudioByTime）

    };

    // 检查音频缓冲状态
    const checkAudioBuffer = useCallback(() => {
        const results = {
            backgroundBuffered: false,
            translatedBuffered: false,
            backgroundBufferTime: 0,
            translatedBufferTime: 0
        };

        if (backgroundAudioRef.current) {
            const buffered = backgroundAudioRef.current.buffered;
            const currentTime = backgroundAudioRef.current.currentTime;
            
            for (let i = 0; i < buffered.length; i++) {
                if (currentTime >= buffered.start(i) && currentTime <= buffered.end(i)) {
                    results.backgroundBuffered = true;
                    results.backgroundBufferTime = buffered.end(i) - currentTime;
                    break;
                }
            }
        }


        return results;
    }, []);

    // 统一的音视频同步函数 - 使用useCallback优化
    const syncAudioToVideo = useCallback(() => {
        const videoTime = sourceVideoRef.current?.currentTime || 0;
        
        // console.log('同步音频到视频时间:', videoTime);
        
        // 检查音频缓冲状态
        const bufferStatus = checkAudioBuffer();
        // console.log('音频缓冲状态:', bufferStatus);
        
        // 同步背景音 - 无论是否在播放都要同步时间
        if (backgroundAudioRef.current) {
            backgroundAudioRef.current.currentTime = videoTime;
            // console.log('背景音已同步到时间:', videoTime);
        }

        const activeTrack = timelineEditorRef.current?.activeTrack || 'translated';
        seekTranslatedTrackBuffer(videoTime, activeTrack === 'translated' && !!sourceVideoRef.current && !sourceVideoRef.current.paused);
        
    }, [checkAudioBuffer, seekTranslatedTrackBuffer]);

    // 监听左右视频就绪状态，实现预加载完成检测
    useEffect(() => {
        if (leftVideoReady && rightVideoReady) {
            // 预加载完成：左右视频都已就绪，可以立即播放
            setIsPreloaded(true);
        } else {
            // 预加载状态检查
            setIsPreloaded(false);
        }
    }, [leftVideoReady, rightVideoReady]);

    // 视频播放控制函数 - 使用useCallback优化
    const togglePlay = useCallback(() => {
        if (sourceVideoRef.current) {
            // 播放按钮点击
            
            // 标记为用户手动操作
            userInteractionRef.current = true;
            
            if (isPlaying) {
                // 暂停视频和音频
                sourceVideoRef.current.pause();
                if (subtitleOverlayPlayerRef.current) {
                    try { subtitleOverlayPlayerRef.current.pause(); } catch {}
                }
                
                // 停止所有音频
                // 1. 停止背景音（只暂停，不销毁HLS实例，以便快速恢复播放）
                if (backgroundAudioRef.current) {
                    backgroundAudioRef.current.pause();
                }
                // 不销毁 backgroundHlsRef，保持HLS实例以便快速恢复播放
                
                // 2. 停止翻译音频
                pauseTranslatedTrackBuffer();
                stopTransAudio();
                if (transAudioRef.current) {
                    transAudioRef.current.pause();
                    transAudioRef.current.currentTime = 0;
                    setCurrentTransAudio(null);
                }
                
                // 3. 停止原始台词音频
                stopOriginAudio();
                if (originAudioRef.current) {
                    originAudioRef.current.pause();
                    originAudioRef.current.currentTime = 0;
                    originAudioRef.current.src = '';
                }
                
                // 4. 停止原人声音频（只暂停，不销毁HLS实例，以便快速恢复播放）
                if (vocalsAudioRef.current) {
                    vocalsAudioRef.current.pause();
                    vocalsAudioRef.current.currentTime = 0;
                }
                // 不销毁 vocalsHlsRef，保持HLS实例以便快速恢复播放
                
                // 5. 停止通过 AudioManager 播放的所有音频（翻译后台词播放）
                if (currentPlayingSubtitleIdRef.current !== null) {
                    try {
                        const audioId = `target_audio_${currentPlayingSubtitleIdRef.current}`;
                        audioManager.stopSound(audioId);
                        audioManager.destroySound(audioId);
                        // 主控制：停止 AudioManager 音频
                    } catch (error) {
                        console.error('停止 AudioManager 音频时出错:', error);
                    }
                    currentPlayingSubtitleIdRef.current = null;
                }
                // 5b. 按所有字幕 ID 遍历停止 AudioManager，避免 ref 滞后或多种前缀漏停
                try {
                    const allAudioIds = ['target_audio', 'segment_audio', 'origin_audio'];
                    allAudioIds.forEach(prefix => {
                        if (videoData?.subtitles) {
                            videoData.subtitles.forEach(subtitle => {
                                const audioId = `${prefix}_${subtitle.id}`;
                                if (audioManager.isPlaying(audioId)) {
                                    audioManager.stopSound(audioId);
                                    audioManager.destroySound(audioId);
                                }
                            });
                        }
                    });
                } catch (error) {
                    console.error('停止 AudioManager 音频时出错:', error);
                }
                
                // 6. 清除单句台词播放实例和状态
                stopCurrentPlayingInstance();
                setSubtitlePlayEndTime(null);
                
                // 重置音频播放请求标记
                audioPlayRequestedRef.current = false;
                // 主控制：暂停左侧视频和所有音频
            } else {
                resetPlaybackFinished();
                // 用户主动点击播放：清除“点击台词跳转”标记，避免播不出当前句（暂停时点台词再点播放的场景）
                isSubtitleClickSeekRef.current = false;
                // 清除防重复播放锁，避免“同一句已播”误判导致不播当前句
                currentPlayingSubtitleIdRef.current = null;
                lastAudioCheckVideoTimeRef.current = 0;
                // 在用户手势栈内初始化背景 Web Audio 并 resume（后续 Promise 链不再算手势）
                if (backgroundAudioEnabled && backgroundAudioRef.current) {
                    ensureBackgroundGainNode();
                    resumeBackgroundAudioContextIfSuspended();
                }
                // 底部播放按钮播放：清除单句台词播放实例和状态
                stopCurrentPlayingInstance();
                setSubtitlePlayEndTime(null);
                setIsPlaying(false);

                const video = sourceVideoRef.current;
                const metaDur =
                    video && Number.isFinite(video.duration) && video.duration > 0 ? video.duration : duration;
                let currentProgress = video.currentTime;
                // 片尾自动停后再点播放：左侧会从 0 重播，但 currentTime/UI 常仍停在末尾，
                // MediaPlaybackManager 节流会把底部时间从尾端慢慢拉回 0，表现为“倒带”；此处一次性对齐。
                if (metaDur > 0 && (video.ended || currentProgress >= metaDur - 0.08)) {
                    resetPlaybackFinished();
                    currentProgress = 0;
                    video.currentTime = 0;
                    if (backgroundAudioRef.current && backgroundAudioRef.current.src) {
                        backgroundAudioRef.current.currentTime = 0;
                    }
                    if (vocalsAudioRef.current && vocalsAudioRef.current.src) {
                        vocalsAudioRef.current.currentTime = 0;
                    }
                    seekTranslatedTrackBuffer(0, false);
                }
                // 主视频刻度与底部时间、时间轴播放头对齐（仅拖轴未走台词跳转时也可能不一致）
                syncPlaybackChromeToTime(currentProgress);

                const currentActiveTrack = timelineEditorRef.current?.activeTrack || 'translated';
                if (currentActiveTrack === 'translated' && translatedTrackBuildState === 'building') {
                    showWarning('翻译音轨正在准备中，准备完成后会自动接管播放');
                }
                if (currentActiveTrack === 'translated' && translatedTrackBuildState === 'error') {
                    showError(translatedTrackBuildError || '翻译音轨构建失败，请稍后重试');
                }
                
                // 先跳转到当前位置
                if (subtitleOverlayPlayerRef.current) {
                    playbackManagerRef.current?.notifyRightSeekStarted();
                    subtitleOverlayPlayerRef.current.seekToTime(currentProgress);
                    // 右侧视频跳转到指定时间
                }
                
                // 检查预加载状态
                if (isPreloaded) {
                    // 快速播放：预加载完成，立即同时播放左右视频
                    
                    // 同时播放左右视频，无需等待
                    const playPromises = [];
                    
                    // 播放左侧视频
                    sourceVideoRef.current.muted = true;
                    playPromises.push(
                        sourceVideoRef.current.play().catch(error => {
                            if (error.name !== 'AbortError') {
                                console.error('左侧视频播放失败:', error);
                            }
                        })
                    );
                    
                    // 同时播放右侧视频
                    if (subtitleOverlayPlayerRef.current) {
                        playPromises.push(
                            new Promise<void>((resolve) => {
                                subtitleOverlayPlayerRef.current!.play();
                                resolve();
                            })
                        );
                    }
                    
                    // 等待两个视频都开始播放
                    Promise.all(playPromises).then(() => {
                        // 快速播放：左右视频同时播放成功
                        
                        // 播放音频轨道（人声+背景音）
                        playAudioTracks();
                        syncAudioToVideo();
                        if (currentActiveTrack === 'translated' && subtitlePlayEndTime === null) {
                            playTranslatedTrackBuffer(currentProgress);
                        }
                    });
                    
                } else {
                    // 传统播放：预加载未完成，使用传统播放方式
                    
                    // 传统播放方式（兼容性处理）
                    sourceVideoRef.current.muted = true;
                    sourceVideoRef.current.play().catch(error => {
                        if (error.name !== 'AbortError') {
                            console.error('左侧视频播放失败:', error);
                        }
                    });
                    
                    // 播放音频轨道（人声+背景音）
                    playAudioTracks();
                    syncAudioToVideo();
                    if (currentActiveTrack === 'translated' && subtitlePlayEndTime === null) {
                        playTranslatedTrackBuffer(currentProgress);
                    }
                }
                
                // 主控制：播放左侧视频（静音）和音频轨道
            }
            setIsPlaying(!isPlaying);
            // 注意：右侧视频的播放状态会通过externalIsPlaying属性自动同步
        }
    }, [
        duration,
        id,
        isPlaying,
        isPreloaded,
        pauseTranslatedTrackBuffer,
        playAudioTracks,
        playTranslatedTrackBuffer,
        seekTranslatedTrackBuffer,
        showError,
        showWarning,
        stopCurrentPlayingInstance,
        syncAudioToVideo,
        syncPlaybackChromeToTime,
        subtitlePlayEndTime,
        translatedTrackBuildError,
        translatedTrackBuildState,
        videoData,
        backgroundAudioEnabled,
        ensureBackgroundGainNode,
        resumeBackgroundAudioContextIfSuspended,
        resetPlaybackFinished,
    ]);

    const forceTrackAudioToTime = useCallback((
        trackType: 'translated' | 'original',
        time: number,
        shouldPlay: boolean
    ) => {
        if (trackType === 'translated') {
            seekTranslatedTrackBuffer(time, shouldPlay);
            if (shouldPlay) {
                playTranslatedTrackBuffer(time);
            }
            return;
        }

        // original
        syncAudioToVideo();
        if (shouldPlay) {
            playAudioTracks();
        }
    }, [playAudioTracks, playTranslatedTrackBuffer, seekTranslatedTrackBuffer, syncAudioToVideo]);

    // 监听时间轴片段选择事件：
    // - 点击任一轨道片段时，切换到对应音轨（含音频）并跳转到片段开始时间
    // - 保持播放状态：原来在播则继续播，原来暂停则保持暂停
    useEffect(() => {
        const handleSegmentSelected = (e: any) => {
            const time = (e && e.detail && typeof e.detail.time === 'number') ? e.detail.time : 0;
            const trackType =
                (e && e.detail && (e.detail.trackType === 'translated' || e.detail.trackType === 'original'))
                    ? (e.detail.trackType as 'translated' | 'original')
                    : 'translated';

            const wasPlaying = !!sourceVideoRef.current && !sourceVideoRef.current.paused;

            // 1) 先把两条轨道可能残留的音频都停掉（不依赖 previousTrack，避免状态不一致时漏停）
            stopAllTimelineAudio();

            // 2) 切轨（含音频初始化），但抑制切轨内部的“自动恢复播放”，避免先在旧时间点响一下
            const prevClickSeek = isSubtitleClickSeekRef.current;
            isSubtitleClickSeekRef.current = true;
            handleSwitchTrack(trackType);
            isSubtitleClickSeekRef.current = prevClickSeek;

            // 3) 再跳转到该片段开始位置；尽量走统一链路更新 UI/进度条/时间轴
            handlePlayheadMove(time);

            // 4) 强制确保音频与点击的轨道对齐，并从 time 开始（保持播放状态）
            forceTrackAudioToTime(trackType, time, wasPlaying);

            // 兜底：如果原来暂停，确保不会被意外播放（例如某些浏览器在 seek 后 auto-play）
            if (!wasPlaying) {
                try { sourceVideoRef.current?.pause(); } catch { /* ignore */ }
            }
        };
        window.addEventListener('timeline:segmentSelected', handleSegmentSelected);
        return () => window.removeEventListener('timeline:segmentSelected', handleSegmentSelected);
    }, [forceTrackAudioToTime, handlePlayheadMove, handleSwitchTrack, stopAllTimelineAudio]);

    // SubtitleOverlayPlayer回调函数 - 使用useCallback优化
    const handleExternalPlay = useCallback(() => {
        // 同步控制左侧视频播放
        // SubtitleOverlayPlayer请求播放，同步左侧视频
        if (sourceVideoRef.current) {
            sourceVideoRef.current.play().catch(error => {
                console.error('左侧视频播放失败:', error);
            });
        }
        if ((timelineEditorRef.current?.activeTrack || 'translated') === 'translated' && subtitlePlayEndTime === null) {
            playTranslatedTrackBuffer(sourceVideoRef.current?.currentTime || 0);
        }
    }, [playTranslatedTrackBuffer, subtitlePlayEndTime]);
    
    // 右侧视频就绪状态回调
    const handleRightVideoReady = useCallback(() => {
        // 右侧视频已就绪
        setRightVideoReady(true);
    }, []);

    const handleExternalPause = useCallback(() => {
        // 同步控制左侧视频暂停
        // SubtitleOverlayPlayer请求暂停，同步左侧视频
        if (sourceVideoRef.current) {
            sourceVideoRef.current.pause();
        }
        pauseTranslatedTrackBuffer();
    }, [pauseTranslatedTrackBuffer]);

    const handleExternalSeek = useCallback((time: number) => {
        // 用户从右侧拖动/seek 时，锁住右侧纠偏，避免 rvfc/管理器回拉导致抖动
        playbackManagerRef.current?.notifyRightSeekStarted();
        // 同步控制左侧视频进度
        // SubtitleOverlayPlayer请求跳转到时间，同步左侧视频
        if (sourceVideoRef.current) {
            sourceVideoRef.current.currentTime = time;
            setPlaybackUiTime(time);
        }
        seekTranslatedTrackBuffer(time, !!sourceVideoRef.current && !sourceVideoRef.current.paused && (timelineEditorRef.current?.activeTrack || 'translated') === 'translated');
    }, [seekTranslatedTrackBuffer]);

    // 右侧视频控制函数
    const toggleRightVideoPlay = useCallback(() => {
        if (subtitleOverlayPlayerRef.current) {
            if (rightVideoIsPlaying) {
                subtitleOverlayPlayerRef.current.pause();
                setRightVideoIsPlaying(false);
                // 右侧视频：暂停
            } else {
                subtitleOverlayPlayerRef.current.play();
                setRightVideoIsPlaying(true);
                // 右侧视频：播放
            }
        }
    }, [rightVideoIsPlaying]);

    const handleRightVideoSeek = useCallback((time: number) => {
        if (subtitleOverlayPlayerRef.current) {
            playbackManagerRef.current?.notifyRightSeekStarted();
            subtitleOverlayPlayerRef.current.seekToTime(time);
            setRightVideoCurrentTime(time);
            // 右侧视频：跳转到时间
        }
    }, []);

    // 监听右侧视频状态变化
    useEffect(() => {
        const interval = setInterval(() => {
            if (subtitleOverlayPlayerRef.current) {
                const currentTime = subtitleOverlayPlayerRef.current.getCurrentTime();
                const duration = subtitleOverlayPlayerRef.current.getDuration();
                const isPlaying = subtitleOverlayPlayerRef.current.isPlaying();
                
                setRightVideoCurrentTime(currentTime);
                setRightVideoDuration(duration);
                setRightVideoIsPlaying(isPlaying);
            }
        }, 100); // 每100ms更新一次状态

        return () => clearInterval(interval);
    }, []);

    // 时间更新防抖
    const timeUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastUpdateTimeRef = useRef<number>(0);

    // 检查音频是否就绪
    const checkAudioReady = () => {
        const backgroundReady = backgroundAudioRef.current && 
            backgroundAudioRef.current.readyState >= 2 && // HAVE_CURRENT_DATA
            backgroundAudioRef.current.src;
        
        const newState = { backgroundReady: !!backgroundReady, vocalsReady: false };
        setAudioReadyState(newState);
        
        // console.log('音频就绪状态检查:', newState);
        return newState.backgroundReady;
    };

    // 暂停台词播放的统一函数
    const pauseSubtitlePlayback = () => {
        // 台词播放：执行暂停流程
        
        // 保存结束时间，用于定位视频和时间轴
        const endTime = subtitlePlayEndTime;
        
        // 清除音频等待定时器
        if (audioReadyTimeoutRef.current) {
            clearTimeout(audioReadyTimeoutRef.current);
            audioReadyTimeoutRef.current = null;
        }
        
        // 同时暂停左右视频和音频，并停在结束时间戳
        if (sourceVideoRef.current) {
            sourceVideoRef.current.pause();
            if (endTime !== null) {
                sourceVideoRef.current.currentTime = endTime;
            }
        }
        if (subtitleOverlayPlayerRef.current) {
            subtitleOverlayPlayerRef.current.pause();
            if (endTime !== null) {
                try {
                    playbackManagerRef.current?.notifyRightSeekStarted();
                    subtitleOverlayPlayerRef.current.seekToTime(endTime);
                } catch {}
            }
        }
        if (backgroundAudioRef.current) {
            backgroundAudioRef.current.pause();
        }
        pauseTranslatedTrackBuffer();
        // 停止原始台词音频和翻译音频
        stopOriginAudio();
        stopTransAudio();
        // 停止所有通过 AudioManager 播放的当句台词音频（与 togglePlay 一致）
        try {
            if (currentPlayingSubtitleIdRef.current !== null) {
                const audioId = `target_audio_${currentPlayingSubtitleIdRef.current}`;
                audioManager.stopSound(audioId);
                audioManager.destroySound(audioId);
            }
            const allAudioIds = ['target_audio', 'segment_audio', 'origin_audio'];
            allAudioIds.forEach(prefix => {
                if (videoData?.subtitles) {
                    videoData.subtitles.forEach(subtitle => {
                        const audioId = `${prefix}_${subtitle.id}`;
                        if (audioManager.isPlaying(audioId)) {
                            audioManager.stopSound(audioId);
                            audioManager.destroySound(audioId);
                        }
                    });
                }
            });
        } catch (error) {
            console.error('停止 AudioManager 音频时出错:', error);
        }
        currentPlayingSubtitleIdRef.current = null;
        
        // 停止原人声音频
        if (vocalsAudioRef.current) {
            vocalsAudioRef.current.pause();
        }
        
        // 清理HLS实例
        if (backgroundHlsRef.current) {
            backgroundHlsRef.current.destroy();
            backgroundHlsRef.current = null;
        }
        if (vocalsHlsRef.current) {
            vocalsHlsRef.current.destroy();
            vocalsHlsRef.current = null;
        }
        
        // 重置音频播放请求标记
        audioPlayRequestedRef.current = false;
        
        // 更新播放状态（同时立即写入共享 store，避免右侧外部状态同步延迟导致继续播放）
        setIsPlaying(false);
        usePlaybackStore.getState().setIsPlaying(false);
        
        // 停止黑色刻度线动画，并定位到结束时间
        if (timelineEditorRef.current) {
            if (timelineEditorRef.current.playheadAnimationId) {
                cancelAnimationFrame(timelineEditorRef.current.playheadAnimationId);
                timelineEditorRef.current.playheadAnimationId = undefined;
            }
            // 将播放头定位到结束时间
            if (endTime !== null) {
                timelineEditorRef.current.currentTime = endTime;
                timelineEditorRef.current.updatePlayheadPosition();
            }
        }
        
        // 清除台词播放结束时间
        setSubtitlePlayEndTime(null);
        
        // 设置刚结束台词播放的标志，防止立即播放翻译音频
        setJustFinishedSubtitlePlay(true);
        
        // 延迟重置标志，给用户一些时间
        setTimeout(() => {
            setJustFinishedSubtitlePlay(false);
            // 台词播放：已重置播放标志，可以正常播放翻译音频
        }, 2000); // 2秒后重置
        
        console.log('台词播放：已暂停播放并同步右侧视频');
    };

    // 安全停止翻译音频 - 只在必要时停止
    const safeStopTransAudio = useCallback(() => {
        if (transAudioRef.current) {
            transAudioRef.current.pause();
            transAudioRef.current.currentTime = 0;
        }
        setCurrentTransAudio(null);
        currentPlayingSubtitleIdRef.current = null;
        currentPlayingSubtitleWindowRef.current = null;
    }, []);
    
    // 更新 safeStopTransAudio ref 以存储最新的函数引用
    useEffect(() => {
        safeStopTransAudioRef.current = safeStopTransAudio;
    }, [safeStopTransAudio]);

    // 音频与视频校对函数
    const checkAndSyncAudio = () => {
        const videoTime = sourceVideoRef.current?.currentTime || 0;
        
        console.log('校对音频与视频时间，视频时间:', videoTime);
        
        // 检查背景音时间差异
        if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
            const audioTime = backgroundAudioRef.current.currentTime;
            const timeDiff = Math.abs(audioTime - videoTime);
            console.log('背景音时间差异:', timeDiff, '音频时间:', audioTime, '视频时间:', videoTime);
            
            // 如果时间差异超过0.1秒，则同步
            if (timeDiff > 0.1) {
                backgroundAudioRef.current.currentTime = videoTime;
                console.log('背景音时间差异过大，已同步到视频时间:', videoTime);
            } else {
                console.log('背景音时间差异在允许范围内，无需同步');
            }
        }
        
    };

    // 自动播放时确保音频开始播放并校对
    const ensureAudioPlayAndSync = async () => {
        console.log('自动播放：确保音频开始播放并校对');
        
        // 检查音频是否已初始化（有音频源）
        const backgroundAudioInitialized = backgroundAudioRef.current && backgroundAudioRef.current.src;
        
        console.log('音频初始化状态:', {
            backgroundAudioInitialized,
            backgroundAudioSrc: backgroundAudioRef.current?.src,
        });
        
        // 如果音频未初始化，先初始化音频轨道
        if (!backgroundAudioInitialized) {
            console.log('自动播放：音频未初始化，先初始化音频轨道');
            playAudioTracks();
            
            // 等待音频初始化完成
            await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // 先确保音频开始播放
        const audioPromises = [];
        
        // 检查背景音（根据开关状态决定是否播放）
        if (backgroundAudioRef.current && backgroundAudioEnabled) {
            if (backgroundAudioRef.current.paused) {
                console.log('自动播放：启动背景音');
                audioPromises.push(backgroundAudioRef.current.play().catch(error => {
                    console.error('自动播放背景音失败:', error);
                }));
            } else {
                console.log('自动播放：背景音已在播放');
            }
        } else {
            if (!backgroundAudioEnabled) {
                console.log('自动播放：背景音开关已关闭，跳过播放背景音');
            } else {
                console.log('自动播放：背景音引用为空');
            }
        }
        
        
        // 等待音频开始播放
        if (audioPromises.length > 0) {
            await Promise.all(audioPromises);
            console.log('自动播放：音频已开始播放');
        }
        
        // 延迟一小段时间确保音频已经开始播放，然后进行校对
        setTimeout(() => {
            checkAndSyncAudio();
            // 注意：右侧视频现在通过等待机制同步，不需要额外的校对
        }, 10);
    };

    // 校对右侧视频与左侧视频的播放画面
    const syncRightVideoToLeft = () => {
        const leftVideoTime = sourceVideoRef.current?.currentTime || 0;
        console.log('校对右侧视频：同步到左侧视频时间', leftVideoTime);
        
        // 通过更新播放时钟来触发右侧视频同步
        setPlaybackUiTime(leftVideoTime);
        
        // 更新进度条显示
        const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
        if (progressBar && duration > 0) {
            const progressPercent = (leftVideoTime / duration) * 100;
            progressBar.style.setProperty('--progress', `${progressPercent}%`);
        }
        
        console.log('右侧视频已同步到左侧视频时间:', leftVideoTime);
    };

    // 增强的校对功能，确保画面完全一致
    const enhancedSyncRightVideoToLeft = () => {
        const leftVideoTime = sourceVideoRef.current?.currentTime || 0;
        console.log('增强校对：开始同步右侧视频到左侧视频时间', leftVideoTime);
        
        // 立即更新状态，触发右侧视频同步
        setPlaybackUiTime(leftVideoTime);
        
        // 更新进度条显示
        const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
        if (progressBar && duration > 0) {
            const progressPercent = (leftVideoTime / duration) * 100;
            progressBar.style.setProperty('--progress', `${progressPercent}%`);
        }
        
        // 延迟再次同步，确保右侧视频完全同步
        setTimeout(() => {
            const currentLeftTime = sourceVideoRef.current?.currentTime || 0;
            console.log('增强校对：二次同步，当前左侧视频时间', currentLeftTime);
            
            // 再次更新状态，确保同步
            setPlaybackUiTime(currentLeftTime);
            
            // 更新进度条
            if (progressBar && duration > 0) {
                const progressPercent = (currentLeftTime / duration) * 100;
                progressBar.style.setProperty('--progress', `${progressPercent}%`);
            }
            
            console.log('增强校对：右侧视频二次同步完成', currentLeftTime);
        }, 100);
        
        console.log('增强校对：右侧视频同步完成', leftVideoTime);
    };

    // 执行原台词播放逻辑（字幕行「播放」按钮）
    const executePlaySubtitle = (timeRange: string) => {
        // 全局唯一播放实例管理：先停止当前播放实例
        stopCurrentPlayingInstance();
        pauseTranslatedTrackBuffer();
        
        // 先完全停止并销毁原人声音频，避免与单句原台词音频重叠
        if (vocalsAudioRef.current) {
            vocalsAudioRef.current.pause();
            vocalsAudioRef.current.currentTime = 0;
            vocalsAudioRef.current.src = ''; // 清空音频源，防止重新播放
        }
        if (vocalsHlsRef.current) {
            vocalsHlsRef.current.destroy();
            vocalsHlsRef.current = null;
        }
        // 设置标志，防止在播放单句原台词期间重新初始化原人声音频
        audioPlayRequestedRef.current = false;
        
        // 若当前整体处于播放状态，先统一暂停，避免状态冲突导致单句播放失效
        if (isPlaying) {
            if (sourceVideoRef.current) sourceVideoRef.current.pause();
            if (subtitleOverlayPlayerRef.current) try { subtitleOverlayPlayerRef.current.pause(); } catch {}
            if (backgroundAudioRef.current) backgroundAudioRef.current.pause();
            stopTransAudio();
            stopOriginAudio();
            setIsPlaying(false);
        }
        console.log('台词播放：开始播放台词', timeRange);
        
        // 重置刚结束台词播放的标志
        setJustFinishedSubtitlePlay(false);
        
        // 解析时间范围
        const [startTimeStr, endTimeStr] = timeRange.split(' → ');
        const startTime = parseTimeToSeconds(startTimeStr);
        const endTime = parseTimeToSeconds(endTimeStr);
        
        console.log('台词播放：时间范围', { startTime, endTime });
        
        // 清除之前的定时器
        if (subtitlePlayTimeoutRef.current) {
            clearTimeout(subtitlePlayTimeoutRef.current);
        }
        
        // 定位刻度线与时间轴视图
        try {
            if (timelineEditorRef.current) {
                timelineEditorRef.current.currentTime = startTime;
                timelineEditorRef.current.updatePlayheadPosition();
                if (typeof (timelineEditorRef.current as any).scrollToTime === 'function') {
                    (timelineEditorRef.current as any).scrollToTime(startTime);
                }
            }
        } catch {}
        // 使用精确同步机制
        const waitForVideosReadyAndPlay = async () => {
            console.log('台词播放：开始精确同步', startTime);
            
            // 更新进度条显示
            const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
            if (progressBar && duration > 0) {
                const progressPercent = (startTime / duration) * 100;
                progressBar.style.setProperty('--progress', `${progressPercent}%`);
            }
            
            setPlaybackUiTime(startTime);
            
            // 使用精确同步启动
            await startPreciseSync(startTime);
            
            console.log('台词播放：精确同步完成，开始播放');
            
            // 现在两个视频都精确同步了，真正同时开始播放
            console.log('台词播放：准备同时启动两个视频');
            
            // 使用requestAnimationFrame确保在同一帧内启动
            await new Promise<void>((resolve) => {
                requestAnimationFrame(() => {
                    const playPromises: Promise<any>[] = [];
                    
                    if (sourceVideoRef.current) {
                        sourceVideoRef.current.muted = true;
                        playPromises.push(
                            sourceVideoRef.current.play().catch(error => {
                                console.error('台词播放：左侧视频播放失败:', error);
                            })
                        );
                    }
                    
                    if (subtitleOverlayPlayerRef.current) {
                        playPromises.push(
                            new Promise<void>((resolve) => {
                                subtitleOverlayPlayerRef.current!.play();
                                // 给右侧视频一点时间启动
                                setTimeout(() => resolve(), 10);
                            })
                        );
                    }
                    
                    // 等待两个视频都开始播放
                    Promise.all(playPromises).then(() => {
                        console.log('台词播放：两个视频已真正同时开始播放，毫秒级同步已启动');
                        resolve();
                    });
                });
            });
            
            // 停止翻译音频播放
            stopTransAudio();
            
            // 播放原始台词音频（纯净人声）
            playOriginAudio(timeRange);
            
            // 全局唯一播放实例管理：记录新的播放实例
            currentPlayingInstanceRef.current = {
                type: 'origin',
                timeRange: timeRange
            };
            console.log('全局播放管理：记录新的原台词播放实例', timeRange);
            
            // 播放原始台词时，暂停背景音，只播放台词
            console.log('播放原始台词：暂停背景音，只播放台词音频');
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
            }
            
            console.log('台词播放：所有媒体已同时开始播放');
        };
        
        // 开始等待并播放流程
        waitForVideosReadyAndPlay();
        
        // 标记为用户手动操作
        userInteractionRef.current = true;
        
        // 立即同步音频到开始时间
        syncAudioToVideo();
        
        // 设置播放状态
        setIsPlaying(true);
        
        // 注意：右侧视频已通过直接控制同步，无需额外同步
        
        // 设置结束时间，用于在时间更新时检查是否需要暂停
        setSubtitlePlayEndTime(endTime);
        
        console.log('台词播放：已设置结束时间', endTime);
    };

    // 场景4：字幕编辑时的音频控制函数
    const pauseAudioForEditing = () => {
        console.log('字幕编辑：暂停所有视频和音频');
        
        // 停止当前播放实例
        stopCurrentPlayingInstance();
        
        // 暂停视频
        if (sourceVideoRef.current) {
            sourceVideoRef.current.pause();
        }
        if (subtitleOverlayPlayerRef.current) {
            try { subtitleOverlayPlayerRef.current.pause(); } catch {}
        }
        
        // 暂停背景音频
        if (backgroundAudioRef.current) {
            backgroundAudioRef.current.pause();
        }
        // 清理背景音频HLS实例（防止HLS实例继续播放）
        if (backgroundHlsRef.current) {
            backgroundHlsRef.current.destroy();
            backgroundHlsRef.current = null;
        }
        
        // 停止翻译音频
        stopTransAudio();
        if (transAudioRef.current) {
            transAudioRef.current.pause();
            transAudioRef.current.currentTime = 0;
        }
        
        // 停止原人声音频
        stopOriginAudio();
        if (originAudioRef.current) {
            originAudioRef.current.pause();
            originAudioRef.current.currentTime = 0;
        }
        
        // 停止原人声音频轨道
        if (vocalsAudioRef.current) {
            vocalsAudioRef.current.pause();
        }
        if (vocalsHlsRef.current) {
            vocalsHlsRef.current.destroy();
            vocalsHlsRef.current = null;
        }
        
        // 停止所有通过 AudioManager 播放的音频
        try {
            if (currentPlayingSubtitleIdRef.current !== null) {
                const audioId = `target_audio_${currentPlayingSubtitleIdRef.current}`;
                audioManager.stopSound(audioId);
                audioManager.destroySound(audioId);
            }
            // 停止所有可能的音频实例
            const allAudioIds = ['target_audio', 'segment_audio', 'origin_audio'];
            allAudioIds.forEach(prefix => {
                // 尝试停止所有可能的音频ID（通过遍历所有字幕ID）
                if (videoData?.subtitles) {
                    videoData.subtitles.forEach(subtitle => {
                        const audioId = `${prefix}_${subtitle.id}`;
                        if (audioManager.isPlaying(audioId)) {
                            audioManager.stopSound(audioId);
                            audioManager.destroySound(audioId);
                        }
                    });
                }
            });
        } catch (error) {
            console.error('停止 AudioManager 音频时出错:', error);
        }
        
        // 清除播放状态
        setSubtitlePlayEndTime(null);
        setIsPlaying(false);
        setCurrentTransAudio(null);
        // 重置音频播放请求标记（防止异步创建的HLS实例自动播放）
        audioPlayRequestedRef.current = false;
        currentPlayingSubtitleIdRef.current = null;
        
        // 停止黑色刻度线动画
        if (timelineEditorRef.current && timelineEditorRef.current.playheadAnimationId) {
            cancelAnimationFrame(timelineEditorRef.current.playheadAnimationId);
            timelineEditorRef.current.playheadAnimationId = undefined;
        }
    };

    const syncAudioAfterEditing = async () => {
        console.log('字幕编辑完成：同步音频并恢复播放');
        
        // 检查音频是否已初始化
        const backgroundAudioInitialized = backgroundAudioRef.current && backgroundAudioRef.current.src;
        
        // 如果音频未初始化，先初始化音频轨道
        if (!backgroundAudioInitialized) {
            console.log('字幕编辑完成：音频未初始化，先初始化音频轨道');
            playAudioTracks();
            
            // 等待音频初始化完成
            await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        syncAudioToVideo();
        // 恢复播放状态
        setIsPlaying(true);
        // 恢复视频播放
        if (sourceVideoRef.current) {
            sourceVideoRef.current.muted = true; // 保持静音
            sourceVideoRef.current.play().catch(error => {
                console.error('恢复视频播放失败:', error);
            });
        }
        // 恢复音频播放（根据开关状态决定是否播放）
        if (backgroundAudioRef.current && backgroundAudioEnabled) {
            backgroundAudioRef.current.play().catch(error => {
                console.error('恢复背景音播放失败:', error);
            });
        } else if (!backgroundAudioEnabled) {
            console.log('恢复播放：背景音开关已关闭，跳过播放背景音');
        }
        // 注意：右侧视频现在通过等待机制同步，不需要额外的校对
    };
    const handleTimeUpdate = () => {
        if (sourceVideoRef.current) {
            const currentTime = sourceVideoRef.current.currentTime;
            const duration = sourceVideoRef.current.duration;
            
            // 验证时间是否有效
            if (isNaN(currentTime) || currentTime < 0) {
                console.warn('检测到无效的当前时间:', currentTime);
                return;
            }
            
            // 检查台词播放是否结束
            if (subtitlePlayEndTime !== null && currentTime + END_EPSILON >= subtitlePlayEndTime) {
                console.log('台词播放：到达结束时间，检查音频状态', { 
                    currentTime, 
                    endTime: subtitlePlayEndTime,
                    audioReady: checkAudioReady(),
                    audioReadyState 
                });
                
                // 立即停止视频和时间轴，停在结束时间戳
                if (sourceVideoRef.current) {
                    sourceVideoRef.current.pause();
                    sourceVideoRef.current.currentTime = subtitlePlayEndTime;
                }
                if (subtitleOverlayPlayerRef.current) {
                    subtitleOverlayPlayerRef.current.pause();
                    try {
                        playbackManagerRef.current?.notifyRightSeekStarted();
                        subtitleOverlayPlayerRef.current.seekToTime(subtitlePlayEndTime);
                    } catch {}
                }
                
                // 全局唯一播放实例管理：清除播放实例记录
                if (currentPlayingInstanceRef.current?.type === 'origin') {
                    console.log('全局播放管理：原台词播放结束，清除播放实例记录');
                    currentPlayingInstanceRef.current = null;
                } else if (currentPlayingInstanceRef.current?.type === 'target') {
                    console.log('全局播放管理：翻译后台词播放结束，清除播放实例记录');
                    currentPlayingInstanceRef.current = null;
                }
                
                // 更新时间轴播放头到结束位置
                if (timelineEditorRef.current) {
                    timelineEditorRef.current.currentTime = subtitlePlayEndTime;
                    timelineEditorRef.current.updatePlayheadPosition();
                    // 停止播放头动画
                    if (timelineEditorRef.current.playheadAnimationId) {
                        cancelAnimationFrame(timelineEditorRef.current.playheadAnimationId);
                        timelineEditorRef.current.playheadAnimationId = undefined;
                    }
                }
                
                // 检查音频是否真正开始播放
                const backgroundPlaying = backgroundAudioRef.current && !backgroundAudioRef.current.paused;
                const hasAudioStarted = backgroundPlaying;
                
                // 如果音频还没开始播放，给一个缓冲时间
                if (!hasAudioStarted && audioReadyState.backgroundReady) {
                    console.log('台词播放：音频已就绪但未开始播放，等待音频启动');
                    
                    // 清除之前的超时定时器
                    if (audioReadyTimeoutRef.current) {
                        clearTimeout(audioReadyTimeoutRef.current);
                    }
                    
                    // 设置一个较短的等待时间，让音频有机会开始播放
                    audioReadyTimeoutRef.current = setTimeout(() => {
                        console.log('台词播放：音频启动等待超时，继续暂停流程');
                        pauseSubtitlePlayback();
                    }, 500); // 500ms等待时间
                    
                    return;
                }
                
                // 如果音频已经开始播放，或者没有音频轨道，直接暂停
                pauseSubtitlePlayback();
                return;
            }
            
            // 字幕渲染已移到rvfc帧内执行，避免阻塞主线程
            // renderSubtitleToCanvas(currentTime);
            
            // 根据选中的轨道播放不同的音频
            const currentActiveTrack = timelineEditorRef.current?.activeTrack || 'translated';
            
            if (currentActiveTrack === 'translated') {
                // 连续翻译主缓冲已接管正常播放，这里只保留时间驱动的 UI 更新。
            } else if (currentActiveTrack === 'original') {
                // 选中"原视频"轨道：播放背景音+原人声音频（整个完整音频，不需要分段）
                // 如果正在播放单句原台词，不要自动播放原人声音频，避免两个声音重叠
                if (subtitlePlayEndTime === null) {
                    // 只有在没有播放单句原台词时，才自动播放原人声音频
                    // 确保原人声音频正在播放
                    const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                    if (vocalsAudioRef.current && vocalsAudioRef.current.paused && isCurrentlyPlaying) {
                        vocalsAudioRef.current.play().catch(error => {
                            console.error('原人声音频播放失败:', error);
                        });
                    }
                    // 如果原人声音频还没有初始化，初始化它
                    if (!vocalsAudioRef.current || !vocalsAudioRef.current.src) {
                        if (videoData?.vocals_hls_url) {
                            initVocalsAudio();
                        }
                    }
                }
            }
            
            // 同步音频轨道时间 - 已禁用以避免播放卡顿
            // if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
            //     backgroundAudioRef.current.currentTime = currentTime;
            // }
            
            // 黑色刻度线移动改由独立的 PlayheadController 驱动，这里不再更新播放头

            // 防抖机制：减少状态更新频率（不影响黑色刻度线）
            const now = Date.now();
            if (now - lastUpdateTimeRef.current < 200) { // 200ms内不重复更新状态，进一步减少频率
                return;
            }

            // 清除之前的定时器
            if (timeUpdateTimeoutRef.current) {
                clearTimeout(timeUpdateTimeoutRef.current);
            }

            // 延迟更新状态，避免频繁同步
            timeUpdateTimeoutRef.current = setTimeout(() => {
                setPlaybackUiTime(currentTime);
                lastUpdateTimeRef.current = Date.now();

                // 更新进度条的CSS变量
                const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
                if (progressBar && duration > 0) {
                    const progressPercent = (currentTime / duration) * 100;
                    progressBar.style.setProperty('--progress', `${progressPercent}%`);
                }

                // 根据当前时间高亮对应的目标台词
                updateTargetSubtitleHighlight(currentTime);
            }, 50); // 50ms延迟
        }
    };

    const handleLoadedMetadata = () => {
        if (sourceVideoRef.current) {
            const videoEl = sourceVideoRef.current;
            const duration = videoEl.duration;
            setDuration(duration);

            // 计算并记录左侧视频宽高比
            const videoWidth = videoEl.videoWidth || 0;
            const videoHeight = videoEl.videoHeight || 0;
            if (videoWidth > 0 && videoHeight > 0) {
                const ratio = Number((videoWidth / videoHeight).toFixed(6));
                // 约简比例（如 1920x1080 -> 16:9）
                const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
                const g = gcd(videoWidth, videoHeight);
                const simplified = `${Math.round(videoWidth / g)}:${Math.round(videoHeight / g)}`;
                setLeftVideoAspect({ width: videoWidth, height: videoHeight, ratio, simplified });
                // 左侧视频分辨率已获取
            }

            // 初始化进度条
            const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
            if (progressBar) {
                progressBar.style.setProperty('--progress', '0%');
            }
            
            // 重新计算时间轴总时长，确保使用视频的实际时长（不向上取整）
            if (timelineEditorRef.current) {
                // 视频元数据加载完成，重新计算时间轴总时长
                
                // 计算字幕的最大结束时间
                let maxEndTime = 0;
                if (videoData?.subtitles && videoData.subtitles.length > 0) {
                    videoData.subtitles.forEach((subtitle: any) => {
                        const endTimeStr = subtitle.post_end_time || subtitle.origin_end_time || '00:00:00,000';
                        const timeParts = endTimeStr.split(':');
                        const hours = parseInt(timeParts[0] || '0');
                        const minutes = parseInt(timeParts[1] || '0');
                        const secondsParts = timeParts[2] ? timeParts[2].split(',') : ['0', '0'];
                        const seconds = parseInt(secondsParts[0] || '0');
                        const milliseconds = parseInt(secondsParts[1] || '0');
                        const endTime = hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
                        if (endTime > maxEndTime) {
                            maxEndTime = endTime;
                        }
                    });
                }
                
                // 使用视频实际时长作为上限，确保不超过视频实际时长（不向上取整）
                const totalTime = Math.max(20, duration);
                console.log('视频实际时长:', duration, '秒，字幕最大结束时间:', maxEndTime, '秒，计算得到的总时长:', totalTime, '秒');
                timelineEditorRef.current.totalTime = totalTime;
                timelineEditorRef.current.generateTimeMarks();
                timelineEditorRef.current.applyZoom();
                
                // 如果有字幕数据，重新初始化字幕片段
                if (videoData?.subtitles && videoData.subtitles.length > 0) {
                    timelineEditorRef.current.initSubtitles(videoData.subtitles);
                }
            }
        }
    };

    // 右侧视频元数据加载，记录右侧宽高比
    // 注意：右侧视频现在由SubtitleOverlayPlayer组件管理，此函数暂时保留但不再使用targetVideoRef
    const handleRightLoadedMetadata = () => {
        // 右侧视频现在由SubtitleOverlayPlayer组件管理，不需要直接操作targetVideoRef
        console.log('右侧视频由SubtitleOverlayPlayer组件管理');
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = parseFloat(e.target.value);
        const duration = sourceVideoRef.current?.duration || 0;
        setPlaybackUiTime(newTime);
        
        // 更新进度条显示
        const progressBar = e.target as HTMLInputElement;
        if (duration > 0) {
            const progressPercent = (newTime / duration) * 100;
            progressBar.style.setProperty('--progress', `${progressPercent}%`);
        }
        
        if (sourceVideoRef.current) {
            sourceVideoRef.current.currentTime = newTime;
            // 主控制：跳转到时间
            
            // 场景1：用户主动跳转时间 - 同步音频
            syncAudioToVideo();
        }
        
        // 优化：直接控制右侧视频跳转，避免通过props传递的延迟
        if (subtitleOverlayPlayerRef.current) {
            playbackManagerRef.current?.notifyRightSeekStarted();
            subtitleOverlayPlayerRef.current.seekToTime(newTime);
            // 主控制：直接跳转右侧视频到时间
        }

        // 根据拖拽时间高亮对应的目标台词
        updateTargetSubtitleHighlight(newTime);
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (sourceVideoRef.current) {
            sourceVideoRef.current.volume = newVolume;
        }
        // 注意：右侧视频现在由SubtitleOverlayPlayer组件管理，音量控制通过回调函数处理
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // 格式化时间（带小数）
    const formatTimeWithDecimals = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = (time % 60).toFixed(1);
        return `${minutes.toString().padStart(2, '0')}:${seconds.padStart(4, '0')}`;
    };

    // 检查视频同步状态
    const checkVideoSync = () => {
        if (sourceVideoRef.current) {
            const leftTime = sourceVideoRef.current.currentTime;
            const leftDuration = sourceVideoRef.current.duration;
            const leftPlaying = !sourceVideoRef.current.paused;
            
            console.log(`视频同步状态检查:
                左侧视频: 时间=${leftTime.toFixed(2)}s, 总时长=${leftDuration.toFixed(2)}s, 播放中=${leftPlaying}
                右侧视频: 由SubtitleOverlayPlayer管理, 外部播放状态=${isPlaying}, 外部时间=${currentTimeRef.current.toFixed(2)}s`);
        }
    };

    // ====== Canvas 字幕渲染降负：预解析时间戳 + 缓存当前字幕段 ======
    const subtitleCanvasCacheRef = useRef<{
        starts: number[];
        ends: number[];
        texts: string[];
    } | null>(null);
    const lastRenderedSubtitleIdxRef = useRef<number>(-1);

    useEffect(() => {
        if (!videoData?.subtitles || videoData.subtitles.length === 0) {
            subtitleCanvasCacheRef.current = null;
            lastRenderedSubtitleIdxRef.current = -1;
            return;
        }

        // 只在 videoData 变化时预解析，避免 rvfc/rAF 每次都重复 parseTimeToSeconds
        const subs = videoData.subtitles;
        const starts: number[] = new Array(subs.length);
        const ends: number[] = new Array(subs.length);
        const texts: string[] = new Array(subs.length);

        for (let i = 0; i < subs.length; i++) {
            const s = subs[i];
            starts[i] = parseTimeToSeconds(s.origin_start_time);
            ends[i] = parseTimeToSeconds(s.origin_end_time);
            texts[i] = s.post_text || s.origin_text || '';
        }

        subtitleCanvasCacheRef.current = { starts, ends, texts };
        lastRenderedSubtitleIdxRef.current = -1; // 强制下一次渲染刷一次
    }, [videoData?.id, videoData?.subtitles]);

    // 渲染字幕到Canvas
    const renderSubtitleToCanvas = (timeInSeconds: number) => {
        const canvas = subtitleCanvasRef.current;
        const cache = subtitleCanvasCacheRef.current;
        if (!canvas || !cache) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // 二分查找：找到最后一个 start <= time 的字幕段
        const { starts, ends, texts } = cache;
        let lo = 0;
        let hi = starts.length - 1;
        let best = -1;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (starts[mid] <= timeInSeconds) {
                best = mid;
                lo = mid + 1;
            } else {
                hi = mid - 1;
            }
        }

        const idx = best >= 0 && timeInSeconds < ends[best] ? best : -1;
        if (idx === lastRenderedSubtitleIdxRef.current) return;

        // 仅在字幕段变化时才重绘，避免同一段内的无意义清空/绘制
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (idx >= 0) {
            const text = texts[idx];
            if (text.trim()) {
                renderSubtitleText(ctx, canvas, text);
            }
        }

        lastRenderedSubtitleIdxRef.current = idx;
    };

    // 渲染字幕文本到Canvas
    const renderSubtitleText = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, text: string) => {
        // 设置字体样式 - 完全按照index.html的样式
        const fontSize = 18; // 固定18px，与index.html一致
        ctx.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // 设置最大宽度（Canvas宽度的90%）
        const maxWidth = canvas.width * 0.9;
        const padding = 16; // 固定16px内边距，与index.html一致
        const lineHeight = 24; // 固定24px行高，与index.html一致
        
        // 计算字幕框位置（底部居中，与index.html一致）
        const x = canvas.width / 2;
        const y = canvas.height * 0.8; // 距离底部20%的位置，与index.html一致

        // 处理文本换行
        const lines = wrapText(ctx, text, maxWidth);
        const totalHeight = lines.length * lineHeight;

        // 不绘制背景 - 与index.html一致（透明背景）
        // 只绘制文字，无背景色

        // 绘制文字（多行）
        ctx.fillStyle = '#ffffff';
        lines.forEach((line, index) => {
            const lineY = y - totalHeight / 2 + (index + 0.5) * lineHeight;
            ctx.fillText(line, x, lineY);
        });
    };

    // 使用字幕渲染控制器 - 在rvfc帧内执行，避免阻塞主线程
    useSubtitleRenderer(sourceVideoRef, renderSubtitleToCanvas);

    // 文本换行：与 SubtitleOverlayPlayer / 原 SubtitleDemo 一致；空格语言按词，无空格（如日语）按字符
    const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
        const tokens = text.split(/(\s+)/);
        const lines: string[] = [];
        let currentLine = '';

        const pushLine = () => {
            const trimmed = currentLine.trim();
            if (trimmed) lines.push(trimmed);
            currentLine = '';
        };

        const appendByChars = (chunk: string) => {
            const chars = Array.from(chunk);
            for (const ch of chars) {
                const testLine = currentLine + ch;
                if (ctx.measureText(testLine).width > maxWidth && currentLine.trim() !== '') {
                    pushLine();
                    currentLine = ch;
                } else {
                    currentLine = testLine;
                }
            }
        };

        for (const token of tokens) {
            if (!token) continue;

            if (/^\s+$/.test(token)) {
                if (currentLine.trim()) currentLine += ' ';
                continue;
            }

            const testLine = currentLine + token;
            if (ctx.measureText(testLine).width <= maxWidth) {
                currentLine = testLine;
                continue;
            }

            if (currentLine.trim()) {
                pushLine();
            }

            if (ctx.measureText(token).width > maxWidth) {
                appendByChars(token);
            } else {
                currentLine = token;
            }
        }

        if (currentLine.trim()) {
            lines.push(currentLine.trim());
        }

        return lines;
    };

    // 调整Canvas尺寸
    // 注意：此函数现在不再使用，因为右侧视频由SubtitleOverlayPlayer组件管理
    const resizeCanvas = () => {
        // console.log('resizeCanvas: 右侧视频现在由SubtitleOverlayPlayer组件管理，此函数不再使用');
        // 右侧视频现在由SubtitleOverlayPlayer组件管理，不需要调整Canvas尺寸
    };

    // 将目标字幕注入右侧视频，使用原生 WebVTT 轨道，字幕显示在视频底部并脱离文档流
    // 注意：此useEffect已完全移除，因为右侧视频由SubtitleOverlayPlayer组件管理

    // 跳转到指定时间（用于点击台词编辑区域，不自动播放）
    // fromKeyboard：由上下键触发时传 true，会缩短“不自动播放”标记的清除延迟，避免按空格/点播放时播不出当前句
    // targetSubtitleId：上下键切换时传入目标台词 id，用于相接/重叠台词时始终按该句居中，否则按时间反推当前句

    const seekToTime = (timeRange: string, fromKeyboard?: boolean, targetSubtitleId?: number) => {
        const startTime = timeRange.split(' → ')[0];
        const timeInSeconds = parseTimeToSeconds(startTime);

        // 设置标记：这是点击台词导致的跳转，不自动播放
        isSubtitleClickSeekRef.current = true;

        // 取消未执行的 timeupdate 延迟回调，避免用旧时间覆盖本次跳转后的高亮（解决下键“弹回”）
        if (timeUpdateTimeoutRef.current) {
            clearTimeout(timeUpdateTimeoutRef.current);
            timeUpdateTimeoutRef.current = null;
        }

        // 【核心修复】：先停止所有正在播放的音频，避免"响一声"
        stopTransAudio();
        stopOriginAudio();
        stopCurrentPlayingInstance();

        // 停止背景音
        if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
            backgroundAudioRef.current.pause();
        }

        // 停止原人声音频
        if (vocalsAudioRef.current && !vocalsAudioRef.current.paused) {
            vocalsAudioRef.current.pause();
        }

        if (sourceVideoRef.current) {
            // 点击台词编辑区域时，先暂停视频，避免自动播放
            if (!sourceVideoRef.current.paused) {
                sourceVideoRef.current.pause();
            }
            sourceVideoRef.current.currentTime = timeInSeconds;
            setPlaybackUiTime(timeInSeconds);
            console.log(`字幕点击跳转：跳转到时间 ${timeInSeconds}秒`);

            // 场景1：用户主动跳转时间 - 同步音频（但不播放）
            syncAudioToVideo();
        }

        // 优化：直接控制右侧视频跳转，避免通过props传递的延迟
        if (subtitleOverlayPlayerRef.current) {
            // 右侧视频也暂停，避免自动播放
            try {
                if (subtitleOverlayPlayerRef.current.isPlaying && subtitleOverlayPlayerRef.current.isPlaying()) {
                    subtitleOverlayPlayerRef.current.pause();
                }
            } catch (e) {
                // 如果 isPlaying 方法不存在或出错，直接尝试暂停
                try {
                    subtitleOverlayPlayerRef.current.pause();
                } catch (pauseError) {
                    // 忽略暂停错误
                }
            }
            playbackManagerRef.current?.notifyRightSeekStarted();
            subtitleOverlayPlayerRef.current.seekToTime(timeInSeconds);
            console.log(`字幕点击跳转：直接跳转右侧视频到时间 ${timeInSeconds}秒`);
        }

        // 确保播放状态为暂停
        setIsPlaying(false);
        setSubtitlePlayEndTime(null);

        // 同步更新时间轴滚动条位置
        if (timelineEditorRef.current) {
            // 延迟更新以确保时间轴内容已更新
            setTimeout(() => {
                timelineEditorRef.current?.updateScrollbar();
            }, 100);
            setTimeout(() => {
                timelineEditorRef.current?.updateScrollbar();
            }, 300);
        }

        // 更新进度条显示
        const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
        if (progressBar && duration > 0) {
            const progressPercent = (timeInSeconds / duration) * 100;
            progressBar.style.setProperty('--progress', `${progressPercent}%`);
        }

        // 同步更新时间轴的播放头位置
        if (timelineEditorRef.current) {
            timelineEditorRef.current.currentTime = timeInSeconds;
            timelineEditorRef.current.updatePlayheadPosition();
            console.log(`字幕点击跳转：同步更新时间轴播放头到时间 ${timeInSeconds}秒`);
        }

        // 高亮对应的目标台词
        updateTargetSubtitleHighlight(timeInSeconds);

        // 将时间轴滚动到对应位置：优先使当前台词片段水平居中，否则按时间百分比滚动
        if (timelineEditorRef.current) {
            if (targetSubtitleId != null && typeof timelineEditorRef.current.scrollToSegmentCenter === 'function') {
                timelineEditorRef.current.scrollToSegmentCenter(targetSubtitleId);
            } else {
                const subs = videoData?.subtitles;
                const activeTrack = timelineEditorRef.current.activeTrack || 'translated';
                const useOriginTimes = isFromPure || (activeTrack === 'original');
                const getStart = (s: { origin_start_time: string; post_start_time?: string }) =>
                    useOriginTimes ? s.origin_start_time : (s.post_start_time || s.origin_start_time);
                const getEnd = (s: { origin_end_time: string; post_end_time?: string }) =>
                    useOriginTimes ? s.origin_end_time : (s.post_end_time || s.origin_end_time);
                const matched = subs?.find(s => {
                    const start = parseTimeToSeconds(getStart(s));
                    const end = parseTimeToSeconds(getEnd(s));
                    return timeInSeconds >= start && timeInSeconds < end;
                });
                if (matched && typeof timelineEditorRef.current.scrollToSegmentCenter === 'function') {
                    timelineEditorRef.current.scrollToSegmentCenter(matched.id);
                } else {
                    timelineEditorRef.current.scrollToTime(timeInSeconds);
                }
            }
        }

        // 清除标记：允许后续按空格/点播放时正常播当前句。键盘跳转用较短延迟，点击台词用较长延迟
        const clearDelay = fromKeyboard ? 280 : 1000;
        setTimeout(() => {
            isSubtitleClickSeekRef.current = false;
        }, clearDelay);
    };

    // 上下键跳转上下句、空格键控制播放/暂停（字幕校对页按原台词，视频编辑页按翻译后台词）
    useEffect(() => {
        const handleVideoEditKeyDown = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
            const role = (e.target as HTMLElement)?.getAttribute?.('role');
            const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select' || role === 'textbox' || (e.target as HTMLElement)?.isContentEditable;
            if (isEditable) return;

            const subs = videoData?.subtitles;
            if (!subs || subs.length === 0) return;

            // 选中原台词用原台词时间，选中翻译后用翻译后时间（字幕校对页仅原台词，视频编辑页按 activeTrack 区分）
            const activeTrack = timelineEditorRef.current?.activeTrack || 'translated';
            const useOriginTimes = isFromPure || (activeTrack === 'original');
            const getStart = (s: { origin_start_time: string; origin_end_time: string; post_start_time?: string; post_end_time?: string }) =>
                useOriginTimes ? s.origin_start_time : (s.post_start_time || s.origin_start_time);
            const getEnd = (s: { origin_start_time: string; origin_end_time: string; post_start_time?: string; post_end_time?: string }) =>
                useOriginTimes ? s.origin_end_time : (s.post_end_time || s.origin_end_time);

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                const now = Date.now();
                if (now - lastVideoEditKeyTimeRef.current < 100) return;
                lastVideoEditKeyTimeRef.current = now;
                const ct = sourceVideoRef.current?.currentTime ?? currentTimeRef.current;
                let prevIdx: number;
                const currentId = highlightedSubtitleId ?? null;
                if (currentId != null) {
                    const idx = subs.findIndex(s => s.id === currentId);
                    prevIdx = idx < 0 ? 0 : idx <= 0 ? 0 : idx - 1;
                } else {
                    const inRangeIdx = subs.findIndex(s => {
                        const start = parseTimeToSeconds(getStart(s));
                        const end = parseTimeToSeconds(getEnd(s));
                        return ct >= start && ct < end;
                    });
                    if (inRangeIdx >= 0) {
                        prevIdx = inRangeIdx <= 0 ? 0 : inRangeIdx - 1;
                    } else {
                        let lastPast = -1;
                        for (let i = subs.length - 1; i >= 0; i--) {
                            if (parseTimeToSeconds(getEnd(subs[i])) <= ct) {
                                lastPast = i;
                                break;
                            }
                        }
                        prevIdx = lastPast < 0 ? 0 : lastPast;
                    }
                }
                const prev = subs[prevIdx];
                const timeRange = `${getStart(prev)} → ${getEnd(prev)}`;
                seekToTime(timeRange, true, prev.id);
                setHighlightedSubtitleId(prev.id);
                scrollToHighlightedSubtitle(prev.id);
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                const now = Date.now();
                if (now - lastVideoEditKeyTimeRef.current < 100) return;
                lastVideoEditKeyTimeRef.current = now;
                const ct = sourceVideoRef.current?.currentTime ?? currentTimeRef.current;
                let nextIdx: number;
                const currentId = highlightedSubtitleId ?? null;
                if (currentId != null) {
                    const idx = subs.findIndex(s => s.id === currentId);
                    nextIdx = idx < 0 ? 0 : Math.min(idx + 1, subs.length - 1);
                } else {
                    const inRangeIdx = subs.findIndex(s => {
                        const start = parseTimeToSeconds(getStart(s));
                        const end = parseTimeToSeconds(getEnd(s));
                        return ct >= start && ct < end;
                    });
                    if (inRangeIdx >= 0) {
                        nextIdx = Math.min(inRangeIdx + 1, subs.length - 1);
                    } else {
                        const firstAhead = subs.findIndex(s => parseTimeToSeconds(getStart(s)) >= ct);
                        nextIdx = firstAhead < 0 ? subs.length - 1 : firstAhead;
                    }
                }
                const next = subs[nextIdx];
                const timeRange = `${getStart(next)} → ${getEnd(next)}`;
                seekToTime(timeRange, true, next.id);
                setHighlightedSubtitleId(next.id);
                scrollToHighlightedSubtitle(next.id);
                return;
            }
            if (e.key === ' ') {
                e.preventDefault();
                togglePlay();
            }
        };
        document.addEventListener('keydown', handleVideoEditKeyDown, { capture: true });
        return () => document.removeEventListener('keydown', handleVideoEditKeyDown, { capture: true } as any);
    }, [videoData?.subtitles, highlightedSubtitleId, isFromPure, seekToTime, togglePlay]);

    const [currentEpisodeInfo, setCurrentEpisodeInfo] = useState({
        name: '',
        sourceLanguage: '',
        targetLanguage: '',
        subtitleErase: false
    });


    // 获取左侧视频URL的函数（类似右侧视频的fetchVideoUrl）
    const fetchLeftVideoUrl = useCallback(async (): Promise<string> => {
        try {
            const fromData = resolveEditPageStreamHlsUrl(videoData);
            if (videoData && fromData) {
                console.log('使用当前videoData中的视频URL:', fromData);
                return fromData;
            }
            
            // 如果没有videoData但有任务ID，则通过API获取
            if (id) {
                console.log('videoData为空，通过API获取视频数据，任务ID:', id);
                const taskData = await getTaskStatusApi(parseInt(id));
                const fromApi = resolveEditPageStreamHlsUrl(taskData);
                if (taskData && fromApi) {
                    console.log('通过API获取到视频URL:', fromApi);
                    return fromApi;
                }
            }
            
            // 如果都没有，返回空字符串
            console.log('未找到视频URL');
            return '';
        } catch (error) {
            console.error('获取左侧视频URL失败:', error);
            return '';
        }
    }, [videoData, id]); // 保持与右侧视频相同的依赖项

    // 初始化左侧视频（类似右侧视频的initVideo）
    const initLeftVideo = useCallback(async () => {
        console.log('【initLeftVideo】开始执行，检查条件:', {
            hasVideoElement: !!sourceVideoRef.current,
            hasVideoData: !!videoData,
            hasStreamHlsUrl: !!resolveEditPageStreamHlsUrl(videoData)
        });
        
        const video = sourceVideoRef.current;
        if (!video) {
            console.log('【initLeftVideo】左侧视频元素不存在，跳过初始化');
            return;
        }
        
        try {
            const url = await fetchLeftVideoUrl();
            console.log('【initLeftVideo】获取到URL:', {
                url: url,
                urlLength: url.length,
                isM3u8: url.endsWith('.m3u8')
            });
            
            if (!url) {
                console.log('【initLeftVideo】左侧视频URL为空，跳过初始化');
                return;
            }
            
            // 检查URL是否发生变化
            if (currentVideoUrlRef.current === url) {
                console.log('【initLeftVideo】左侧视频URL未变化，跳过初始化:', url);
                return;
            }
            
            console.log('【initLeftVideo】开始初始化左侧视频播放器:', url);
            currentVideoUrlRef.current = url;
            
            // 不需要清理之前的HLS实例，像右侧视频一样让浏览器自动管理
            
            // 设置视频源（像右侧视频一样，不保存HLS实例引用）
            if (url.endsWith('.m3u8')) {
                console.log('【initLeftVideo】检测到m3u8格式，开始HLS处理');
                // HLS视频流
                if (video.canPlayType('application/vnd.apple.mpegurl')) {
                    // Safari原生支持HLS
                    video.src = url;
                    console.log('【initLeftVideo】左侧视频：使用Safari原生HLS支持');
                } else if (Hls.isSupported()) {
                    // 使用HLS.js库（像右侧视频一样，不保存引用）
                    console.log('【initLeftVideo】使用HLS.js库处理m3u8');
                    const Hls = (await import('hls.js')).default;
                    const hls = new Hls();
                    hls.loadSource(url);
                    hls.attachMedia(video);
                    console.log('【initLeftVideo】左侧视频：使用HLS.js库播放HLS视频流');
                } else {
                    console.error('【initLeftVideo】左侧视频：浏览器不支持HLS视频播放');
                    video.src = url; // 尝试直接设置，可能会失败
                }
            } else {
                // 普通视频文件
                video.src = url;
                console.log('【initLeftVideo】左侧视频：播放普通视频文件');
            }
            
            // 预加载视频元数据，确保readyState >= 1
            video.preload = 'metadata';
            video.load(); // 强制加载元数据
            
            // 监听视频就绪状态
            const handleCanPlay = () => {
                console.log('【initLeftVideo】左侧视频已就绪，可以播放');
                setLeftVideoReady(true);
                video.removeEventListener('canplay', handleCanPlay);
            };
            
            video.addEventListener('canplay', handleCanPlay);
            
            console.log('【initLeftVideo】左侧视频源已设置完成:', {
                url: url,
                hlsInstance: !!hlsRef.current,
                videoSrc: video.src
            });
            
        } catch (error) {
            console.error('【initLeftVideo】左侧视频初始化失败:', error);
        }
    }, [fetchLeftVideoUrl]);

    useEffect(() => {
        const init = async () => {
            // 开始初始化左侧视频
            
            const streamHlsUrl = resolveEditPageStreamHlsUrl(videoData);
            // 右侧就绪且任一流地址可用时再初始化左侧（与右侧 pure / composite 回退策略一致）
            if (rightVideoReady && videoData && streamHlsUrl) {
                // 如果HLS实例已经存在且URL没有变化，跳过初始化
                if (hlsRef.current && currentVideoUrlRef.current === streamHlsUrl) {
                    console.log('【左侧视频初始化】HLS实例已存在且URL未变化，跳过初始化');
                    return;
                }
                
                console.log('【左侧视频初始化】右侧视频已就绪，videoData和HLS URL都存在，开始初始化左侧视频');
                await initLeftVideo();
            } else if (videoData && streamHlsUrl && !rightVideoReady) {
                // 数据与流已齐但右侧未就绪，便于排查卡死；首屏缺数据/缺流不刷屏
                console.log('【左侧视频初始化】等待右侧就绪:', {
                    rightVideoReady,
                    hasVideoData: !!videoData,
                    hasStreamHlsUrl: !!streamHlsUrl
                });
            }
        };
        
        init();
        
        // 不需要清理函数，像右侧视频一样让浏览器自动管理HLS实例
    }, [
        rightVideoReady,
        videoData?.video_hls_url,
        videoData?.videoHlsUrl,
        videoData?.pure_video_hls_url,
        videoData?.pureVideoHlsUrl,
    ]);

    // 提前初始化音频轨道 - 在视频数据加载完成后立即初始化
    useEffect(() => {
        if (!videoData) {
            return;
        }

        // 开始提前初始化音频轨道
        
        // 检查是否需要初始化背景音
        if (videoData.backgroud_audio_hls_url && backgroundAudioRef.current && !backgroundAudioRef.current.src) {
            // backgroud_audio_hls_url 已经在 getTaskStatusApi 中处理过了，包含 /storage 前缀
            // 如果已经是完整URL或已包含代理前缀，直接使用；否则使用 processVideoUrl 处理
            const backgroundAudioUrl = videoData.backgroud_audio_hls_url.startsWith('http://') || videoData.backgroud_audio_hls_url.startsWith('https://') || videoData.backgroud_audio_hls_url.startsWith('/storage') || videoData.backgroud_audio_hls_url.startsWith('/media')
                ? videoData.backgroud_audio_hls_url
                : processVideoUrl(videoData.backgroud_audio_hls_url);
            // 预加载背景音
            
            if (backgroundAudioUrl.includes('.m3u8')) {
                // HLS格式音频预加载
                import('hls.js').then((Hls) => {
                    if (Hls.default.isSupported()) {
                        if (backgroundHlsRef.current) {
                            backgroundHlsRef.current.destroy();
                        }
                        const hls = new Hls.default({
                            enableWorker: true,
                            lowLatencyMode: true, // 启用低延迟模式
                        });
                        backgroundHlsRef.current = hls;
                        hls.loadSource(backgroundAudioUrl);
                        hls.attachMedia(backgroundAudioRef.current!);
                        hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                            // 背景音HLS清单解析完成，音频已预加载
                        });
                    }
                });
            } else {
                // 普通音频格式预加载
                backgroundAudioRef.current.src = backgroundAudioUrl;
                backgroundAudioRef.current.load();
            }
        }


        // 翻译音频预取窗口：由 maintainPrefetchWindow 动态维护（维持后续 5 条音频就绪）
        // 这里只做一次初始化触发，避免固定 slice(0,3) 的静态预取
        if (videoData.subtitles && videoData.subtitles.length > 0) {
            maintainPrefetchWindow(sourceVideoRef.current?.currentTime || 0);
        }
    }, [videoData]);

    // 视频数据变化时重新初始化HLS
    useEffect(() => {
        // 只有在videoData和任一流式 HLS URL 都存在时才重新初始化
        if (!videoData || !resolveEditPageStreamHlsUrl(videoData)) {
            // videoData或HLS URL不存在，跳过重新初始化
            return;
        }
        
        if (sourceVideoRef.current) {
            console.log('视频数据已更新，重新初始化HLS播放器');
            // 延迟一点时间确保DOM已更新
            const timer = setTimeout(() => {
                // 触发HLS重新初始化
                const event = new Event('videoDataUpdated');
                window.dispatchEvent(event);
            }, 200);
            
            return () => clearTimeout(timer);
        }
    }, [videoData?.video_hls_url]);

    return {
        // State
        isLoadingVideo,
        setIsLoadingVideo,
        showBackgroundVolumePopup,
        setShowBackgroundVolumePopup,
        backgroundVolumePopupPosition,
        setBackgroundVolumePopupPosition,
        backgroundVolume,
        setBackgroundVolume,
        isPreloaded,
        setIsPreloaded,
        leftVideoReady,
        setLeftVideoReady,
        rightVideoReady,
        setRightVideoReady,
        backgroundAudioEnabled,
        setBackgroundAudioEnabled,
        isPlaying,
        setIsPlaying,
        isPlayingRef,
        playbackManagerRef,
        duration,
        setDuration,
        rightVideoIsPlaying,
        setRightVideoIsPlaying,
        rightVideoCurrentTime,
        setRightVideoCurrentTime,
        rightVideoDuration,
        setRightVideoDuration,
        subtitlePlayEndTime,
        setSubtitlePlayEndTime,
        subtitlePlayEndTimeRef,
        audioReadyState,
        setAudioReadyState,
        leftVideoAspect,
        setLeftVideoAspect,
        rightVideoAspect,
        setRightVideoAspect,
        volume,
        setVolume,
        currentSubtitleAudio,
        setCurrentSubtitleAudio,
        currentTransAudio,
        setCurrentTransAudio,
        translatedTrackBuildState,
        setTranslatedTrackBuildState,
        translatedTrackPlayerRef,
        // Refs
        sourceVideoRef,
        targetVideoRef,
        subtitleOverlayPlayerRef,
        subtitleCanvasRef,
        hlsRef,
        currentVideoUrlRef,
        isSubtitleClickSeekRef,
        lastVideoEditKeyTimeRef,
        backgroundAudioRef,
        vocalsAudioRef,
        // Functions
        togglePlay,
        seekToTime,
        handlePlayheadMove,
        handleSwitchTrack,
        toggleBackgroundAudio,
        pauseAudioForEditing,
        syncAudioAfterEditing,
        playSubtitle: executePlaySubtitle,
        playTargetSubtitle: executePlayTargetSubtitle,
        seekTranslatedTrackBuffer,
        pauseTranslatedTrackBuffer,
        playTranslatedTrackBuffer,
        stopTransAudio,
        stopCurrentPlayingInstance,
        playAudioTracks,
        ensureAudioPlayAndSync,
        syncRightVideoToLeft,
        handleTimeUpdate,
        handleVideoEnded,
        handleLoadedMetadata,
        handleRightLoadedMetadata,
        handleRightVideoReady,
        handleExternalPlay,
        handleExternalPause,
        handleExternalSeek,
        toggleRightVideoPlay,
        handleRightVideoSeek,
        handleSeek,
        handleVolumeChange,
        formatTime,
        formatTimeWithDecimals,
        fetchLeftVideoUrl,
        initLeftVideo,
        userInteractionRef,
        subtitlePlayTimeoutRef,
        ensureBackgroundGainNode,
        resumeBackgroundAudioContextIfSuspended,
        backgroundGainNodeRef,
        vocalsHlsRef,
        currentPlayingInstanceRef,
        stopOriginAudio,
    };
};
